import { Component, OnInit, AfterViewInit, OnDestroy, Inject, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, TouchedChangeEvent, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { forkJoin, take, Subject, debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';
import { takeUntil, map, catchError } from 'rxjs/operators';
import {
  GeneralLanguage,
  OrganizationProjectService,
  OrganisationDropdownsResponse,
  RegionDropdownItem,
  RegionOrderItem,
  OrganisationProfile,
  CreateUpdateOrganisationRequest,
  OrganisationTeamMember,
  OrganisationPartner,
  OrganisationImageItem,
  OrganisationMediaDto,
  GeneralService,
  FileUploadResponse,
  CharityProfileService,
  ProfileFieldsResponse,
  AllProfileFieldsResponse,
  DynamicTranslationService,
  GsUserService,
  AnalyticsService,
  AuthService
} from '@gs-2.0/core';
import { ToastService } from '../../services/toast.service';
import { CHARITY_FORM_CONFIG, buildFormGroupStructure, getFieldsByGroup, getFieldsByTab, FormGroupName, FormTab, SCHEMA_GROUP_PATH } from './charity-form.config';
import { DropdownItem } from '../dropdown/dropdown.component';
import { MediaPreviewDialogComponent } from '../media-preview-dialog/media-preview-dialog.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { MonthDayDatepickerHeaderComponent } from './month-day-datepicker-header.component';
import { MonthDayDateAdapter } from './month-day-date-adapter';
import { ImageCropperComponent } from '../image-cropper/image-cropper.component';

// Custom date format for MM/DD display only
export const MONTH_DAY_FORMAT: MatDateFormats = {
  parse: {
    dateInput: 'input',
  },
  display: {
    dateInput: 'input',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
    monthLabel: 'MMMM'
  },
};

export interface CharityFormData {
  language: GeneralLanguage;
  organizationUuid?: string; // For edit mode
}

export enum FormStep {
  DETAILS = 0,
  TEAM = 1,
  FINANCIALS = 2,
  DOCUMENTS = 3,
  GALLERY = 4
}

export interface BudgetItem {
  id: string;
  itemName: string;
  budget: number | null;
  actual: number | null;
}

export interface BudgetYear {
  id: string;
  year: string;
  fiscalYearEnd: string;
  currency: string;
  revenueItems: BudgetItem[];
  expenseItems: BudgetItem[];
}

// Form controls whose value is tracked outside the form (uploadedCoverImage,
// uploadedLogo). Their controls always remain '', so Validators.required would
// permanently invalidate the form. Required-ness is checked at submit via the
// uploaded* state instead.
const SHADOW_FILE_UPLOAD_FIELDS = new Set<string>(['coverPhoto', 'logo']);

@Component({
  selector: 'app-charity-form',
  standalone: false,
  templateUrl: './charity-form.component.html',
  styleUrls: ['./charity-form.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: MonthDayDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MONTH_DAY_FORMAT }
  ]
})
export class CharityFormComponent implements OnInit, AfterViewInit, OnDestroy {
  FormStep = FormStep;
  currentStep: FormStep = FormStep.DETAILS;
  customDatepickerHeader = MonthDayDatepickerHeaderComponent;
  detailsForm!: FormGroup;
  teamForm!: FormGroup;
  financialsForm!: FormGroup;
  isLoadingDropdowns = true;
  dropdowns: OrganisationDropdownsResponse | null = null;
  profiles: OrganisationProfile[] = [];
  currentYear = new Date().getFullYear();
  isCountriesDropdownOpen = false;
  selectedCountries: string[] = [];
  // Ordered region UUIDs derived from selectedCountries; reorderable via drag/drop.
  selectedRegions: string[] = [];
  isCategoriesDropdownOpen = false;
  selectedCategories: string[] = [];
  maxCategoriesLimit = 10;
  isCausesDropdownOpen = false;
  selectedCause: string | null = null;
  organisationUuid: string | null = null;

  // Edit mode properties
  isEditMode = false;
  isLoadingOrganisation = false;
  organisationData: any = null;
  entityStatus = 'DRAFT';

  get isDraftStatus(): boolean {
    return this.entityStatus === 'DRAFT';
  }

  // Toast debouncing
  private lastToastMessage: string | null = null;
  private lastToastTime: number = 0;
  private readonly TOAST_DEBOUNCE_MS = 500; // Don't show same toast within 500ms

  // Team step data
  charityLeader: OrganisationTeamMember | null = null;
  executiveTeamMembers: OrganisationTeamMember[] = [];
  existingExecutiveFormGroups: FormGroup[] = [];
  boardMembers: OrganisationTeamMember[] = [];
  existingBoardMemberFormGroups: FormGroup[] = [];
  partnerOrganizations: OrganisationPartner[] = [];
  existingPartnerFormGroups: FormGroup[] = [];
  editingExecutiveIndex: number | null = null;
  editingBoardMemberIndex: number | null = null;
  selectedProfileUuid: string | null = null;

  // Field visibility configuration from profile
  fieldVisibility: Map<string, 'optional' | 'mandatory' | 'hidden'> = new Map();
  private schemaFlat: { [key: string]: any } | null = null;

  // Financial step data
  budgetYears: BudgetYear[] = [];
  currentBudgetYearIndex: number = 0;

  // Dropdown state for Year and Currency
  isYearDropdownOpen = false;
  isCurrencyDropdownOpen = false;
  yearDropdownItems: DropdownItem[] = [];
  currencyDropdownItems: DropdownItem[] = [];
  selectedYearItem: DropdownItem | null = null;
  selectedCurrencyItem: DropdownItem | null = null;
  fiscalYearPickerStartDate: Date = new Date();

  // Financials validation errors
  financialsYearError: string = '';
  financialsFiscalYearError: string = '';
  financialsCurrencyError: string = '';
  financialsItemValidationAttempted: boolean = false;

  // Track which budget year index has errors
  currentErrorBudgetYearIndex: number = -1;

  // Tracks whether the user tried to advance from Documents/Gallery without uploading
  documentsNextAttempted = false;
  galleryNextAttempted = false;

  // New properties for JIRA requirements
  hasBeenSavedOnce = false;
  completedSteps: Set<FormStep> = new Set();
  showErrorSummary = false;
  errorMessages: string[] = [];
  isPreviewAvailable = false;
  charityNameError: string | null = null;
  isCheckingCharityName = false;
  isCharityAdmin = false;

  /**
   * The Giving Link is an internally-managed field: only a Foundation Admin or a
   * Super Admin may edit it. Everyone else gets it disabled + readonly in the
   * template, and buildGivingLinkPayloadValue() re-asserts the server value on
   * submit so a devtools-enabled input can't smuggle an edit through.
   */
  canEditGivingLink = false;

  /** Giving Link as last loaded from the API — the value non-editors always send back. */
  private serverGivingLink = '';

  /**
   * Roles allowed to edit the Giving Link. Both foundation-admin spellings are in
   * use across the codebase (see settings/projects.component.ts), so accept either.
   */
  private static readonly GIVING_LINK_EDITOR_ROLES = [
    'GS_SUPERADMIN',
    'GS_FOUNDATION_ADMIN',
    'GS_ADMIN_FOUNDATION'
  ];

  private destroy$ = new Subject<void>();

  // ─────────────────────────────────────────────────
  // GA4 per-tab analytics tracking (form_start/form_complete/form_partial/
  // form_abandon/form_error) — see docs/charity-form-tab-analytics/.
  // ─────────────────────────────────────────────────
  private static readonly TAB_BY_STEP: Record<FormStep, FormTab> = {
    [FormStep.DETAILS]: 'details',
    [FormStep.TEAM]: 'team',
    [FormStep.FINANCIALS]: 'financials',
    [FormStep.DOCUMENTS]: 'documents',
    [FormStep.GALLERY]: 'gallery',
  };
  // Fixed 1-based order, independent of the dynamically-recomputed `steps[].number`,
  // so GA4 comparisons stay meaningful across orgs with different visible-tab sets.
  private static readonly FORM_STEP_NUMBER: Record<FormStep, number> = {
    [FormStep.DETAILS]: 1,
    [FormStep.TEAM]: 2,
    [FormStep.FINANCIALS]: 3,
    [FormStep.DOCUMENTS]: 4,
    [FormStep.GALLERY]: 5,
  };
  private tabsStarted = new Set<FormStep>();
  private tabsCompleted = new Set<FormStep>();
  // True only while data is being loaded/auto-selected programmatically
  // (populateFormsWithOrganisationData, selectDefaultProfile) so those
  // patchValue calls never register as the user "starting" a tab.
  private isPopulatingForm = false;

  private formNameFor(step: FormStep): string {
    return `charity_${CharityFormComponent.TAB_BY_STEP[step]}`;
  }

  private trackTabStart(step: FormStep): void {
    if (this.isPopulatingForm || this.tabsStarted.has(step)) return;
    this.tabsStarted.add(step);
    this.analyticsService.trackFormStart(this.formNameFor(step), CharityFormComponent.FORM_STEP_NUMBER[step]);
  }

  private trackTabComplete(step: FormStep): void {
    if (this.tabsCompleted.has(step) || !this.hasStepData(step)) return;
    this.tabsCompleted.add(step);
    this.analyticsService.trackFormComplete(this.formNameFor(step), CharityFormComponent.FORM_STEP_NUMBER[step]);
  }

  /**
   * Fires form_complete for every tab with data, unconditionally — bypassing
   * the tabsCompleted dedup that governs normal tab-to-tab navigation. A
   * successful submit is validated end-to-end (onSubmit() already checked
   * every tab before calling executeSubmission()), so it's the authoritative
   * "this data is complete" moment and must not be silently suppressed by
   * tabsCompleted state seeded from a prior session (see
   * docs/charity-form-analytics-reopen-state/) or by earlier navigation in
   * this session. A tab that already fired form_complete via navigation will
   * fire it again here — an accepted trade-off for guaranteed consistency.
   */
  private trackAllTabsCompleteOnSubmit(): void {
    ([FormStep.DETAILS, FormStep.TEAM, FormStep.FINANCIALS, FormStep.DOCUMENTS, FormStep.GALLERY] as FormStep[])
      .filter(step => this.hasStepData(step))
      .forEach(step => {
        this.tabsCompleted.add(step);
        this.analyticsService.trackFormComplete(this.formNameFor(step), CharityFormComponent.FORM_STEP_NUMBER[step]);
      });
  }

  private trackFieldError(step: FormStep, fieldName: string): void {
    this.analyticsService.trackFormError(this.formNameFor(step), CharityFormComponent.FORM_STEP_NUMBER[step], fieldName);
  }

  private trackFieldErrors(step: FormStep, fieldNames: string[]): void {
    fieldNames.forEach(name => this.trackFieldError(step, name));
  }

  /** Fires form_partial (or form_abandon) for every started-but-not-completed tab. */
  private trackInProgressTabs(sendEvent: (formName: string, formStep: number) => void): void {
    this.tabsStarted.forEach(step => {
      if (!this.tabsCompleted.has(step)) {
        sendEvent(this.formNameFor(step), CharityFormComponent.FORM_STEP_NUMBER[step]);
      }
    });
  }

  /**
   * Broader than hasStepData(): true if this tab has ANY prior data at all,
   * even if not every profile-required field is filled yet. Only Financials
   * can have this gap (a saved-as-draft budget year missing some required
   * fields) — Details/Team/Documents/Gallery collapse to hasStepData()
   * itself, since their loaded state can't be "partial" in a way
   * hasStepData() misses (organizationName is required for any successful
   * save; Team/Documents/Gallery only ever reload already-committed/
   * successful entries).
   */
  private hasAnyStepData(step: FormStep): boolean {
    if (step === FormStep.FINANCIALS) return this.budgetYears.length > 0;
    return this.hasStepData(step);
  }

  /**
   * After loading a previously-saved charity (edit mode), mark every tab
   * that already has data as "started" — per the confirmed decision, a tab
   * with any prior data never fires form_start again this session — and,
   * where fully complete, "completed" too, so navigating away from it in
   * this new session doesn't refire form_complete.
   */
  private seedTabTrackingFromLoadedData(): void {
    ([FormStep.DETAILS, FormStep.TEAM, FormStep.FINANCIALS, FormStep.DOCUMENTS, FormStep.GALLERY] as FormStep[]).forEach(step => {
      if (this.hasAnyStepData(step)) this.tabsStarted.add(step);
      if (this.hasStepData(step)) this.tabsCompleted.add(step);
    });
  }

  /** Called from the (ngModelChange) on Financials revenue/expense line-item inputs — see template. */
  markFinancialsItemStarted(): void {
    this.trackTabStart(FormStep.FINANCIALS);
  }

  /** Names of every currently-invalid Details-tab field, per CHARITY_FORM_CONFIG. */
  private getInvalidDetailsFieldNames(): string[] {
    return getFieldsByTab('details')
      .filter(f => !SHADOW_FILE_UPLOAD_FIELDS.has(f.name))
      .filter(f => this.detailsForm.get(`${f.group}.${f.name}`)?.invalid)
      .map(f => f.name);
  }

  /**
   * Names of every currently-invalid Team-tab field: the mini-form controls
   * (excluding "add new" groups, per isTeamFormValidForSubmit's own exclusion)
   * plus every invalid control in already-committed executive/board/partner
   * FormGroups, mapped back to their config field name.
   */
  private getInvalidTeamFieldNames(): string[] {
    const addNewGroups: FormGroupName[] = ['executiveTeam', 'boardMembers', 'partnerOrganizations'];
    const miniFormInvalid = getFieldsByTab('team')
      .filter(f => !addNewGroups.includes(f.group))
      .filter(f => this.teamForm.get(f.name)?.invalid)
      .map(f => f.name);

    const committedInvalid: string[] = [];
    const collect = (groups: FormGroup[], fieldMap: { control: string; configName: string }[]) => {
      groups.forEach(fg => {
        fieldMap.forEach(({ control, configName }) => {
          if (fg.get(control)?.invalid) committedInvalid.push(configName);
        });
      });
    };
    collect(this.existingExecutiveFormGroups, [
      { control: 'fullName', configName: 'executiveName' },
      { control: 'email', configName: 'executiveEmail' },
      { control: 'bio', configName: 'executiveBio' },
      { control: 'linkedinProfile', configName: 'executiveLinkedin' },
    ]);
    collect(this.existingBoardMemberFormGroups, [
      { control: 'fullName', configName: 'boardMemberName' },
      { control: 'email', configName: 'boardMemberEmail' },
      { control: 'bio', configName: 'boardMemberBio' },
      { control: 'linkedinProfile', configName: 'boardMemberLinkedin' },
    ]);
    collect(this.existingPartnerFormGroups, [
      { control: 'name', configName: 'partnerName' },
      { control: 'email', configName: 'partnerEmail' },
      { control: 'relationshipDescription', configName: 'partnerDescription' },
    ]);

    return [...miniFormInvalid, ...committedInvalid];
  }

  /**
   * Fires form_error the first time a Details/Team field becomes touched
   * while invalid — covers plain "left a required field empty" / "typed a
   * malformed email and moved on" cases that no click handler observes.
   * Relies on Angular's native TouchedChangeEvent, which only fires once per
   * false→true transition, so this naturally fires once per field per
   * invalid transition (not on every subsequent blur/submit while the same
   * mistake remains uncorrected). Also covers the custom category/country/
   * cause dropdowns for free, since their close handlers already call
   * .markAsTouched() (see closeCountriesDropdown() etc.).
   */
  private wireFieldErrorTracking(): void {
    const wireControl = (step: FormStep, fieldName: string, control: AbstractControl | null) => {
      if (!control) return;
      control.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
        if (event instanceof TouchedChangeEvent && event.touched && control.invalid) {
          this.trackFieldError(step, fieldName);
        }
      });
    };

    getFieldsByTab('details')
      .filter(f => !SHADOW_FILE_UPLOAD_FIELDS.has(f.name))
      .forEach(f => wireControl(FormStep.DETAILS, f.name, this.detailsForm.get(`${f.group}.${f.name}`)));

    getFieldsByTab('team')
      .forEach(f => wireControl(FormStep.TEAM, f.name, this.teamForm.get(f.name)));
  }

  // Dedup for Financials line-item blur tracking — keyed by `${item.id}:${requiredFieldKey}`.
  // Financials items are plain ngModel-bound objects with no native touched
  // state, so this Set stands in for Angular's touched-transition guard to
  // give the same "fire once per invalid transition" behavior.
  private reportedFinancialsItemErrors = new Set<string>();

  /** Called from (blur) on each Financials revenue/expense line-item input — see template. */
  onFinancialsItemFieldBlur(item: BudgetItem, requiredFieldKey: string, isEmpty: boolean): void {
    const key = `${item.id}:${requiredFieldKey}`;
    const isInvalid = this.isFinancialsFieldRequired(requiredFieldKey) && isEmpty;
    if (isInvalid && !this.reportedFinancialsItemErrors.has(key)) {
      this.reportedFinancialsItemErrors.add(key);
      this.trackFieldError(FormStep.FINANCIALS, requiredFieldKey);
    } else if (!isInvalid) {
      this.reportedFinancialsItemErrors.delete(key);
    }
  }

  // Multi-select search filters
  categorySearchText = '';
  countrySearchText = '';
  causeSearchText = '';

  // Track visible chips limit (all computed dynamically based on container width)
  maxVisibleCategoryChips = 999;
  maxVisibleCountryChips = 999;

  @ViewChild('categoryChipsContainerEl') categoryChipsContainerEl?: ElementRef<HTMLElement>;
  private categoryChipsResizeObserver?: ResizeObserver;

  @ViewChild('countryChipsContainerEl') countryChipsContainerEl?: ElementRef<HTMLElement>;
  private countryChipsResizeObserver?: ResizeObserver;

  @ViewChild('coverImageCropper') coverImageCropper!: ImageCropperComponent;

  // File upload properties
  uploadedLogo: { fileName: string; fileUrl: string; fileUuid: string } | null = null;
  uploadedCoverImage: { fileName: string; fileUrl: string; fileUuid: string } | null = null;
  coverPhotoError = false;
  isUploadingLogo = false;
  isUploadingCoverImage = false;
  pendingUploadFileInputId: string | null = null;
  private pendingCoverImageFileName = '';

  // Team tab file upload properties
  uploadedLeaderPicture: { fileName: string; fileUrl: string; fileUuid: string; objectUuid?: string } | null = null;
  isUploadingLeaderPicture = false;
  uploadedExecutivePicture: { fileName: string; fileUrl: string; fileUuid: string; objectUuid?: string } | null = null;
  isUploadingExecutivePicture = false;
  uploadedBoardMemberPicture: { fileName: string; fileUrl: string; fileUuid: string; objectUuid?: string } | null = null;
  isUploadingBoardMemberPicture = false;
  uploadingExistingExecutiveIndex: number | null = null;
  uploadingExistingBoardMemberIndex: number | null = null;

  // Documents tab properties
  uploadedDocuments: Array<{
    fileName: string;
    fileUrl: string;
    fileUuid: string;
    fileSize: number;
    fileSizeFormatted: string;
    status: 'success' | 'error';
    errorMessage?: string;
  }> = [];
  isUploadingDocument = false;
  readonly SUPPORTED_DOCUMENT_TYPES = [
    'text/plain',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ];
  readonly SUPPORTED_DOCUMENT_EXTENSIONS = ['txt', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

  // Gallery tab properties
  uploadedGalleryMedia: Array<{
    fileName: string;
    fileUrl: string;
    fileUuid: string;
    fileSize: number;
    fileSizeFormatted: string;
    fileType: string; // image/* or video/*
    status: 'success' | 'error';
    errorMessage?: string;
  }> = [];
  isUploadingGalleryMedia = false;
  readonly SUPPORTED_GALLERY_TYPES = [
    // Images
    'image/jpeg',
    'image/bmp',
    'image/png',
    'image/svg+xml',
    'image/webp',
    // Videos
    'video/mp4',
    'video/quicktime',
    'video/mpeg',
    'video/ogg',
    'video/x-msvideo',
    'video/webm',
    'video/3gpp',
    'video/3gpp2',
    'video/avi'
  ];
  readonly SUPPORTED_GALLERY_EXTENSIONS = [
    // Images
    'jpg', 'jpeg', 'bmp', 'png', 'svg', 'webp',
    // Videos
    'mp4', 'mov', 'mpeg', 'mpg', 'ogg', 'avi', 'webm', '3gp', '3g2'
  ];

  steps: Array<{ step: FormStep; label: string; number: number }> = [];

  constructor(
    public dialogRef: MatDialogRef<CharityFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CharityFormData,
    private fb: FormBuilder,
    private organizationProjectService: OrganizationProjectService,
    private toastService: ToastService,
    private generalService: GeneralService,
    private charityProfileService: CharityProfileService,
    private translationService: DynamicTranslationService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private gsUserService: GsUserService,
    private analyticsService: AnalyticsService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initializeSteps();
    this.initializeForm();
    this.applyGivingLinkPermission();
    this.initializeTeamForm();
    this.initializeFinancialsForm();

    this.detailsForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.trackTabStart(FormStep.DETAILS);
    });
    this.teamForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.trackTabStart(FormStep.TEAM);
    });
    this.wireFieldErrorTracking();

    this.gsUserService.getUserInfo()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(response => {
        if (response.response.role === 'GS_ORGANIZATION_ADMIN' && this.data.organizationUuid) {
          this.isCharityAdmin = true;
          // emitEvent:false — disabling is a status change, not a value edit;
          // without this, detailsForm.valueChanges fires (it bubbles up from
          // the disabled control), which wireFieldErrorTracking()'s sibling
          // subscription in ngOnInit would mistake for the user starting the
          // Details tab merely from opening the dialog.
          this.detailsForm?.get('basicInformation.organizationName')?.disable({ emitEvent: false });
        }
      });

    this.detailsForm.get('basicInformation.organizationName')?.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
      switchMap((value: string) => {
        this.charityNameError = null;
        const trimmed = (value || '').trim();
        if (trimmed.length < 3) {
          this.isCheckingCharityName = false;
          return of(null);
        }
        this.isCheckingCharityName = true;
        return this.organizationProjectService.checkOrganisationName(trimmed, this.organisationUuid);
      })
    ).subscribe(result => {
      this.isCheckingCharityName = false;
      if (result?.response === true) {
        this.charityNameError = this.translationService.instant('settings.charity_details_form.error_charity_name_taken');
      }
    });

    // Check if we're in edit mode
    if (this.data.organizationUuid) {
      this.isEditMode = true;
      this.organisationUuid = this.data.organizationUuid;
      this.loadOrganisationDataForEdit();
    } else {
      // Create mode - load standard dropdowns
      this.loadDropdowns();
    }
  }

  ngAfterViewInit(): void {
    // Set up ResizeObserver on the multiselect inputs to recompute visible chips when container resizes
    setTimeout(() => {
      const categoryInputEl = this.categoryChipsContainerEl?.nativeElement?.parentElement;
      if (categoryInputEl) {
        this.categoryChipsResizeObserver = new ResizeObserver(() => {
          this.scheduleCategoryChipsUpdate();
        });
        this.categoryChipsResizeObserver.observe(categoryInputEl);
      }

      const countryInputEl = this.countryChipsContainerEl?.nativeElement?.parentElement;
      if (countryInputEl) {
        this.countryChipsResizeObserver = new ResizeObserver(() => {
          this.scheduleCountryChipsUpdate();
        });
        this.countryChipsResizeObserver.observe(countryInputEl);
      }

    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.categoryChipsResizeObserver?.disconnect();
    this.countryChipsResizeObserver?.disconnect();
  }

  /**
   * Initialize the details form dynamically from config
   * Initialize steps with translations
   */
  private initializeSteps(): void {
    this.steps = [
      { step: FormStep.DETAILS, label: this.translationService.instant('settings.charity_details_form.step_details'), number: 1 },
      { step: FormStep.TEAM, label: this.translationService.instant('settings.charity_details_form.step_team'), number: 2 },
      { step: FormStep.FINANCIALS, label: this.translationService.instant('settings.charity_details_form.step_financials'), number: 3 },
      { step: FormStep.DOCUMENTS, label: this.translationService.instant('settings.charity_details_form.step_documents'), number: 4 },
      { step: FormStep.GALLERY, label: this.translationService.instant('settings.charity_details_form.step_gallery'), number: 5 },
    ];
  }

  /**
   * Charity details form group, used to bind charityDetails controls that are
   * rendered outside the charityDetails section (e.g. Giving Link).
   */
  get charityDetailsGroup(): FormGroup {
    return this.detailsForm.get('charityDetails') as FormGroup;
  }

  /**
   * Initialize the details form
   */
  private initializeForm(): void {
    const detailsGroups: FormGroupName[] = ['basicInformation', 'address', 'socialMediaLinks', 'charityDetails'];
    const formGroupsConfig: any = {};

    detailsGroups.forEach(groupName => {
      const fields = getFieldsByGroup(groupName);
      const groupControls: any = {};

      fields.forEach(field => {
        groupControls[field.name] = [field.defaultValue, field.validators || []];
      });

      formGroupsConfig[groupName] = this.fb.group(groupControls);
    });

    this.detailsForm = this.fb.group(formGroupsConfig);
  }

  /**
   * Initialize the team form dynamically from config
   */
  private initializeTeamForm(): void {
    const teamGroups: FormGroupName[] = ['charityLeader', 'teamInformation', 'executiveTeam', 'boardMembers', 'partnerOrganizations'];
    const teamControls: any = {};

    teamGroups.forEach(groupName => {
      const fields = getFieldsByGroup(groupName);
      fields.forEach(field => {
        teamControls[field.name] = [field.defaultValue, field.validators || []];
      });
    });

    this.teamForm = this.fb.group(teamControls);
  }

  /**
   * Initialize the financials form
   */
  private initializeFinancialsForm(): void {
    this.financialsForm = this.fb.group({
      // Current budget year fields
      year: [''],
      fiscalYearEnd: [null], // Use null for date picker
      currency: [''],
    });

    // Start with no budget years - user clicks "Add New Year" to create first one
  }

  /**
   * Load organisation dropdowns and profiles from API
   */
  private loadDropdowns(): void {
    this.isLoadingDropdowns = true;

    forkJoin({
      dropdowns: this.organizationProjectService.getOrganisationDropdowns(),
      profiles: this.organizationProjectService.getOrganisationProfiles()
    }).subscribe({
      next: (response) => {
        this.dropdowns = response.dropdowns;
        this.profiles = response.profiles.response;
        this.isLoadingDropdowns = false;

        // Populate dropdown items for Year and Currency
        this.populateDropdownItems();

        // Find and pre-select the default profile
        this.selectDefaultProfile();
      },
      error: (error) => {
        console.error('Failed to load organisation dropdowns and profiles:', error);
        // Set empty arrays as fallback
        this.dropdowns = {
          categories: [],
          currencies: [],
          countries: [],
          regions: [],
          champions: [],
          causes: [],
          budgetYears: [],
          projectManagers: []
        };
        this.profiles = [];
        this.isLoadingDropdowns = false;
      }
    });
  }

  /**
   * Find and select the default profile
   */
  private selectDefaultProfile(): void {
    if (!this.profiles || this.profiles.length === 0) {
      return;
    }

    // Find the profile with default: true
    const defaultProfile = this.profiles.find(profile => profile.default === true);

    if (defaultProfile) {
      // Set the form control value to the default profile's UUID (auto-selected,
      // not user-driven — must not register as the user starting the Details tab).
      this.isPopulatingForm = true;
      this.detailsForm.patchValue({
        basicInformation: {
          charityProfile: defaultProfile.uuid
        }
      });
      this.isPopulatingForm = false;

      // Store the UUID and load profile configuration
      this.selectedProfileUuid = defaultProfile.uuid;
      this.loadProfileFieldConfiguration(defaultProfile.uuid);
    } else {
      console.log('No default profile found in the profiles list');
    }
  }

  /**
   * Load organisation data for edit mode
   * Fetches both organisation details and dropdowns in parallel
   */
  private loadOrganisationDataForEdit(): void {
    this.isLoadingOrganisation = true;
    this.isLoadingDropdowns = true;

    forkJoin({
      organisationData: this.organizationProjectService.getOrganisationForEdit(
        this.data.organizationUuid!,
        this.data.language.code
      ),
      dropdowns: this.organizationProjectService.getOrganisationDropdownsWithContext(
        this.data.organizationUuid!
      ),
      profiles: this.organizationProjectService.getOrganisationProfiles()
    }).subscribe({
      next: (response) => {
        this.isEditMode = true;
        this.organisationData = response.organisationData.response;
        this.entityStatus = (this.organisationData?.organisationStatus || 'DRAFT').toUpperCase();
        this.dropdowns = response.dropdowns;
        this.profiles = response.profiles.response;

        this.populateDropdownItems();

        // Set profile and load field configuration
        if (this.organisationData.mandatoryProfile?.uuid) {
          this.selectedProfileUuid = this.organisationData.mandatoryProfile.uuid;
          this.loadProfileFieldConfiguration(this.selectedProfileUuid!);
        }

        // Populate all forms with data
        this.populateFormsWithOrganisationData();
        // A tab that already has data from a prior session must not refire
        // form_start/form_complete merely because this is a fresh dialog
        // instance — see docs/charity-form-analytics-reopen-state/.
        this.seedTabTrackingFromLoadedData();

        this.isLoadingOrganisation = false;
        this.isLoadingDropdowns = false;
        this.hasBeenSavedOnce = true;
        this.isPreviewAvailable = true;
      },
      error: (error) => {
        console.error('Failed to load organisation data:', error);
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_load_charity_data_failed'));
        this.isLoadingOrganisation = false;
        this.isLoadingDropdowns = false;
      }
    });
  }

  /**
   * Navigate to a specific step
   * Auto-saves as draft on first navigation
   */
  goToStep(step: FormStep): void {
    // Validate organization name when navigating to any tab
    this.validateCharityName();

    if (!this.steps.find(s => s.step === step)) return;

    if (!this.hasBeenSavedOnce && step !== FormStep.DETAILS) {
      this.saveAsDraftAndNavigate(step);
    } else {
      this.trackTabComplete(this.currentStep);
      this.currentStep = step;
      this.cdr.detectChanges();
    }
  }

  /**
   * Check if a step is active
   */
  isStepActive(step: FormStep): boolean {
    return this.currentStep === step;
  }

  /**
   * Check if a step is completed
   */
  isStepCompleted(step: FormStep): boolean {
    return this.completedSteps.has(step);
  }

  /**
   * Check if step has valid data (should show accent color indicator)
   * Accent color means: user has added valid data to the tab
   */
  hasStepData(step: FormStep): boolean {
    switch (step) {
      case FormStep.DETAILS:
        return this.hasValidDetailsData();
      case FormStep.TEAM:
        return this.hasValidTeamData();
      case FormStep.FINANCIALS:
        return this.hasValidFinancialsData();
      case FormStep.DOCUMENTS:
        return this.hasValidDocumentsData();
      case FormStep.GALLERY:
        return this.hasValidGalleryData();
      default:
        return false;
    }
  }

  /**
   * Check if Details tab has valid data
   */
  private hasValidDetailsData(): boolean {
    // Check if form is valid and has been touched/filled
    if (!this.detailsForm) {
      return false;
    }

    // Check if organization name is filled (required field)
    const basicInfo = this.detailsForm.get('basicInformation');
    const orgName = basicInfo?.get('organizationName')?.value;

    return orgName && orgName.trim() !== '';
  }

  /**
   * Check if Team tab has valid data
   */
  private hasValidTeamData(): boolean {
    // Check if any team members have been added
    const hasLeader = this.charityLeader !== null;
    const hasExecutiveTeam = this.executiveTeamMembers.length > 0;
    const hasBoardMembers = this.boardMembers.length > 0;
    const hasPartners = this.partnerOrganizations.length > 0;

    return hasLeader || hasExecutiveTeam || hasBoardMembers || hasPartners;
  }

  /**
   * Check if Financials tab has valid data
   * Valid data = at least one budget year has all required fields filled
   * Uses dynamic field configuration when available, falls back to hardcoded required fields
   * Revenue/expense items are optional and don't affect tab color
   */
  private hasValidFinancialsData(): boolean {
    // If no budget years, no data
    if (this.budgetYears.length === 0) {
      return false;
    }

    // Check if any budget year has all required fields filled
    return this.budgetYears.some(year => {
      // Check each field dynamically using the helper method
      const yearRequired = this.isFinancialsFieldRequired('budgetYear');
      const fiscalYearRequired = this.isFinancialsFieldRequired('fiscalYearEnd');
      const currencyRequired = this.isFinancialsFieldRequired('budgetCurrency');

      // Only check fields that are required
      const yearValid = !yearRequired || (year.year && year.year.trim() !== '');
      const fiscalYearValid = !fiscalYearRequired || (year.fiscalYearEnd && year.fiscalYearEnd.trim() !== '');
      const currencyValid = !currencyRequired || (year.currency && year.currency.trim() !== '');

      // All required fields must be valid
      return yearValid && fiscalYearValid && currencyValid;
    });
  }

  /**
   * Check if Documents tab has valid data
   */
  hasValidDocumentsData(): boolean {
    // Check if there are any successfully uploaded documents
    return this.uploadedDocuments.some(doc => doc.status === 'success');
  }

  /**
   * Check if Gallery tab has valid data
   */
  hasValidGalleryData(): boolean {
    // Check if there are any successfully uploaded gallery media
    return this.uploadedGalleryMedia.some(media => media.status === 'success');
  }

  /**
   * Navigate to next step
   */
  onNext(): void {
    // Validate current step before proceeding
    if (this.currentStep === FormStep.DETAILS) {
      const missingCover = this.isFieldRequired('coverPhoto') && !this.uploadedCoverImage;
      const missingLogo = this.isFieldRequired('logo') && !this.uploadedLogo;
      if (this.detailsForm.invalid || missingCover || missingLogo) {
        this.detailsForm.markAllAsTouched();
        this.validateCharityName();
        if (missingCover) this.coverPhotoError = true;
        if (missingCover || missingLogo) {
          this.toastService.error(this.translationService.instant('toast.validation_error'));
        }
        // detailsForm.invalid fields are tracked by wireFieldErrorTracking() via
        // markAllAsTouched() just above — coverPhoto/logo have no real
        // validators (see SHADOW_FILE_UPLOAD_FIELDS) so they need an explicit call.
        if (missingCover) this.trackFieldError(FormStep.DETAILS, 'coverPhoto');
        if (missingLogo) this.trackFieldError(FormStep.DETAILS, 'logo');
        return;
      }
    }

// Save as draft on first next click
    if (!this.hasBeenSavedOnce) {
      const nextStep = this.getNextVisibleStep() ?? FormStep.TEAM;
      this.saveAsDraftAndNavigate(nextStep);
    } else {
      // Mark current step as completed
      this.completedSteps.add(this.currentStep);
      const nextStep = this.getNextVisibleStep();
      if (nextStep !== null) {
        this.trackTabComplete(this.currentStep);
        this.currentStep = nextStep;
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Navigate to previous step
   */
  onBack(): void {
    const prevStep = this.getPreviousVisibleStep();
    if (prevStep !== null) {
      this.trackTabComplete(this.currentStep);
      this.currentStep = prevStep;
    }
  }

  /**
   * Check if back button should be disabled
   */
  isBackDisabled(): boolean {
    return this.getPreviousVisibleStep() === null;
  }

  /**
   * Determine if the page header should be shown for the current step
   * Currently shown for all tabs (can be customized per tab if needed)
   */
  shouldShowPageHeader(): boolean {
    // Show page header for all tabs
    return true;

    // Alternative: Show only for specific tabs
    // return this.currentStep === FormStep.FINANCIALS ||
    //        this.currentStep === FormStep.DETAILS ||
    //        this.currentStep === FormStep.TEAM;
  }

  /**
   * Get the page title based on the current step
   */
  getPageTitle(): string {
    switch (this.currentStep) {
      case FormStep.DETAILS:
        return 'settings.charity_details_form.details_page_title';
      case FormStep.TEAM:
        return 'settings.charity_details_form.team_page_title';
      case FormStep.FINANCIALS:
        return 'settings.charity_details_form.financials.page_title';
      case FormStep.DOCUMENTS:
        return 'settings.charity_details_form.documents_page_title';
      case FormStep.GALLERY:
        return 'settings.charity_details_form.gallery_page_title';
      default:
        return '';
    }
  }

  /**
   * Save as draft and navigate to target step
   */
  private saveAsDraftAndNavigate(targetStep: FormStep): void {
    if (this.detailsForm.invalid) {
      this.detailsForm.markAllAsTouched();
      this.validateCharityName();
      this.showDebouncedErrorToast(this.translationService.instant('settings.charity_details_form.error_fill_required_fields'));
      // markAllAsTouched() above already triggers wireFieldErrorTracking() for
      // every invalid field — no separate enumeration needed here.
      return;
    }

    const formValue = this.getFlattenedFormValues();
    const teamFormValue = this.teamForm.value;

    // Build charity leader from current form values
    const charityLeader = this.buildCharityLeaderFromForm();

    // Build the request payload
    const payload: CreateUpdateOrganisationRequest = {
      organisationUuid: this.organisationUuid,
      mandatoryProfileUuid: formValue.charityProfile || this.selectedProfileUuid || '',
      organisationName: formValue.organizationName || '',
      isPrivate: formValue.private || false,
      address: {
        streetName: formValue.streetName || '',
        streetNumber: null,
        city: formValue.city || '',
        country: this.dropdowns?.countries.find(c => c.uuid === formValue.country)?.name || formValue.country || '',
        countryCode: null,
        latitude: null,
        longitude: null,
        stateProvince: formValue.stateProvince || '',
        postalCode: '',
        landmarkAddress: formValue.landmark || null
      },
      organisationWebsite: formValue.website || '',
      socialMediaLinkedin: formValue.linkedin || null,
      socialMediaTwitter: formValue.twitter || null,
      socialMediaFacebook: formValue.facebook || null,
      socialMediaInstagram: formValue.instagram || null,
      cause: formValue.cause?.[0] ? this.dropdowns?.causes.find(c => c.uuid === formValue.cause[0]) || null : null,
      organisationCategories: formValue.category?.map((uuid: string) =>
        this.dropdowns?.categories.find(c => c.uuid === uuid)
      ).filter((item: any) => item !== undefined) || [],
      targetPopulation: formValue.targetPopulation || null,
      organisationInfluencedCountries: formValue.influencedCountries?.map((uuid: string) =>
        this.dropdowns?.countries.find(c => c.uuid === uuid)
      ).filter((item: any) => item !== undefined) || [],
      globalInfluence: this.isGlobalInfluenceSelected,
      regionOrder: this.buildRegionOrderPayload(),
      yearFounded: formValue.yearFounded || null,
      givingLink: this.buildGivingLinkPayloadValue(formValue.givingLink),
      organisationVideo: null,
      organisationImages: this.buildOrganisationImagesPayload(),
      executiveSummary: formValue.executiveSummary || '',
      visionStatement: formValue.visionStatement || null,
      missionStatement: formValue.missionStatement || null,
      organisationValues: null,
      programAndActivities: '',
      history: formValue.history || null,
      organisationLeader: charityLeader,
      modifiedFields: {},
      numberOfEmployees: parseInt(teamFormValue.numberOfTeamMembers) || 0,
      numberOfVolunteers: 0,
      executiveTeams: this.buildExecutiveTeamPayload(),
      boardMembers: this.buildBoardMemberPayload(),
      partnerOrganisations: this.buildPartnerPayload(),
      budgetOrganisations: this.prepareBudgetPayload(),
      submitted: false,
      languageUuid: this.data.language.uuid,
      organisationDocuments: []
    };

    // Call the API
    this.organizationProjectService.createOrUpdateOrganisation(payload).subscribe({
      next: (response) => {
        // Save the organisation UUID for subsequent updates
        if (response.response?.uuid) {
          this.organisationUuid = response.response.uuid;
        }

        this.hasBeenSavedOnce = true;
        this.isPreviewAvailable = true;
        this.completedSteps.add(FormStep.DETAILS);

        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_charity_added'));

        // Navigate to target step
        this.trackTabComplete(this.currentStep);
        this.currentStep = targetStep;
      },
      error: (error) => {
        console.error('Failed to save charity:', error);
        if (error.error?.message === 'NAME_EXISTS') {
          this.toastService.error(this.translationService.instant('settings.charity_details_form.error_charity_name_taken'));
          this.trackFieldError(FormStep.DETAILS, 'organizationName');
        } else {
          this.toastService.error(this.translationService.instant('settings.charity_details_form.error_failed_to_save'));
        }
      }
    });
  }

  /**
   * Get flattened form values for easier access
   */
  private getFlattenedFormValues() {
    const basicInfo = (this.detailsForm.get('basicInformation') as FormGroup)?.getRawValue() || {};
    const address = this.detailsForm.get('address')?.value || {};
    const socialMedia = this.detailsForm.get('socialMediaLinks')?.value || {};
    const charityDetails = (this.detailsForm.get('charityDetails') as FormGroup)?.getRawValue() || {};

    return {
      // Basic Information
      charityProfile: basicInfo.charityProfile,
      organizationName: basicInfo.organizationName,
      private: basicInfo.private,

      // Address
      streetName: address.streetName,
      city: address.city,
      country: address.country,
      stateProvince: address.stateProvince,
      landmark: address.landmark,

      // Social Media
      linkedin: socialMedia.linkedin,
      twitter: socialMedia.twitter,
      facebook: socialMedia.facebook,
      instagram: socialMedia.instagram,

      // Charity Details
      website: charityDetails.website,
      category: charityDetails.category,
      influencedCountries: charityDetails.influencedCountries,
      cause: charityDetails.cause,
      targetPopulation: charityDetails.targetPopulation,
      yearFounded: charityDetails.yearFounded,
      givingLink: charityDetails.givingLink,
      executiveSummary: charityDetails.executiveSummary,
      visionStatement: charityDetails.visionStatement,
      missionStatement: charityDetails.missionStatement,
      history: charityDetails.history
    };
  }

  /**
   * Only a Foundation Admin / Super Admin may edit the Giving Link. For every other
   * role the control is disabled here (which also renders `disabled` on the input) —
   * the template additionally binds `readonly`, and buildGivingLinkPayloadValue()
   * is the authoritative guard for the submitted value.
   */
  private applyGivingLinkPermission(): void {
    this.canEditGivingLink = CharityFormComponent.GIVING_LINK_EDITOR_ROLES.some(
      role => this.authService.hasRole(role)
    );
    if (!this.canEditGivingLink) {
      // emitEvent:false — see the note on the organizationName disable in ngOnInit.
      this.detailsForm?.get('charityDetails.givingLink')?.disable({ emitEvent: false });
    }
  }

  /**
   * Resolves the Giving Link to submit. Non-editors always send back the value the
   * API last returned, so re-enabling the input via browser devtools and typing a new
   * URL cannot change what is persisted (the form reads charityDetails with
   * getRawValue(), which includes disabled controls, so the value itself can't be trusted).
   */
  private buildGivingLinkPayloadValue(formGivingLink: string | null | undefined): string {
    if (!this.canEditGivingLink) {
      return this.serverGivingLink;
    }
    return formGivingLink || '';
  }

  /**
   * Validate charity name
   */
  validateCharityName(): void {
    const organizationName = this.detailsForm.get('basicInformation.organizationName')?.value;

    if (!organizationName || organizationName.trim() === '') {
      this.charityNameError = this.translationService.instant('settings.charity_details_form.error_charity_name_required');
    }
  }

  /**
   * Show error toast with debouncing to prevent duplicate toasts
   */
  private showDebouncedErrorToast(message: string): void {
    const now = Date.now();

    // Check if we should show this toast (debounce same messages)
    const shouldShow = this.lastToastMessage !== message || (now - this.lastToastTime) > this.TOAST_DEBOUNCE_MS;

    if (shouldShow) {
      // Always dismiss any existing toast first to prevent glitchy repositioning
      this.toastService.dismiss();

      // Small delay to ensure clean dismissal before showing new toast
      setTimeout(() => {
        this.toastService.error(message);
        this.lastToastMessage = message;
        this.lastToastTime = Date.now();
      }, 50);
    }
  }

  /**
   * Build charity leader from form values
   */
  private buildCharityLeaderFromForm(): OrganisationTeamMember | null {
    const teamFormValue = this.teamForm.value;

    // Only build if we have at least name and email
    if (!teamFormValue.leaderName && !teamFormValue.leaderEmail) {
      return null;
    }

    const bio = teamFormValue.leaderBio || '';

    const leaderUuid = this.uploadedLeaderPicture?.objectUuid || this.charityLeader?.uuid;

    return {
      ...(leaderUuid && { uuid: leaderUuid }),
      position: 0,
      fullName: teamFormValue.leaderName || '',
      profilePicture: {
        fileType: 'image',
        allowedFileType: 'image',
        cover: false,
        pendingUpload: false,
        isFormatValid: true,
        progress: 0,
        ...(this.uploadedLeaderPicture?.fileUuid && { uuid: this.uploadedLeaderPicture.fileUuid }),
        ...(this.uploadedLeaderPicture?.fileUrl && { url: this.uploadedLeaderPicture.fileUrl }),
        ...(this.uploadedLeaderPicture?.fileName && { fileName: this.uploadedLeaderPicture.fileName }),
      },
      bio: bio,
      linkedinProfile: teamFormValue.leaderLinkedin || '',
      email: teamFormValue.leaderEmail || ''
    };
  }

  /**
   * Save as draft
   */
  onSaveAsDraft(): void {
    // Mark all fields as touched to surface inline required errors
    this.detailsForm.markAllAsTouched();
    this.markTeamFormTouched();
    this.cdr.detectChanges();

    // Validate organization name is required
    if (!this.detailsForm.get('basicInformation.organizationName')?.value ||
        this.detailsForm.get('basicInformation.organizationName')?.value.trim() === '') {
      this.validateCharityName();
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_org_name_required'));
      // markAllAsTouched() above already triggers wireFieldErrorTracking()
      // for organizationName since it's already invalid at this point.
      return;
    }

    const formValue = this.getFlattenedFormValues();
    // Validate budget years - check for partial data
    if (!this.validateAllBudgetYears()) {
      // Switch to financials tab to show the error
      this.currentStep = FormStep.FINANCIALS;
      this.toastService.error(this.translationService.instant('toast.validation_error'));
      return;
    }

    const teamFormValue = this.teamForm.value;

    // Build charity leader from current form values
    const charityLeader = this.buildCharityLeaderFromForm();

    // Build the request payload
    const payload: CreateUpdateOrganisationRequest = {
      organisationUuid: this.organisationUuid,
      mandatoryProfileUuid: formValue.charityProfile || this.selectedProfileUuid || '',
      organisationName: formValue.organizationName || '',
      isPrivate: formValue.private || false,
      address: {
        streetName: formValue.streetName || '',
        streetNumber: null,
        city: formValue.city || '',
        country: this.dropdowns?.countries.find(c => c.uuid === formValue.country)?.name || formValue.country || '',
        countryCode: null,
        latitude: null,
        longitude: null,
        stateProvince: formValue.stateProvince || '',
        postalCode: '',
        landmarkAddress: formValue.landmark || null
      },
      organisationWebsite: formValue.website || '',
      socialMediaLinkedin: formValue.linkedin || null,
      socialMediaTwitter: formValue.twitter || null,
      socialMediaFacebook: formValue.facebook || null,
      socialMediaInstagram: formValue.instagram || null,
      cause: formValue.cause?.[0] ? this.dropdowns?.causes.find(c => c.uuid === formValue.cause[0]) || null : null,
      organisationCategories: formValue.category?.map((uuid: string) =>
        this.dropdowns?.categories.find(c => c.uuid === uuid)
      ).filter((item: any) => item !== undefined) || [],
      targetPopulation: formValue.targetPopulation || null,
      organisationInfluencedCountries: formValue.influencedCountries?.map((uuid: string) =>
        this.dropdowns?.countries.find(c => c.uuid === uuid)
      ).filter((item: any) => item !== undefined) || [],
      globalInfluence: this.isGlobalInfluenceSelected,
      regionOrder: this.buildRegionOrderPayload(),
      yearFounded: formValue.yearFounded || null,
      givingLink: this.buildGivingLinkPayloadValue(formValue.givingLink),
      organisationVideo: null,
      organisationImages: this.buildOrganisationImagesPayload(),
      executiveSummary: formValue.executiveSummary || '',
      visionStatement: formValue.visionStatement || null,
      missionStatement: formValue.missionStatement || null,
      organisationValues: null,
      programAndActivities: '',
      history: formValue.history || null,
      organisationLeader: charityLeader,
      modifiedFields: {},
      numberOfEmployees: parseInt(teamFormValue.numberOfTeamMembers) || 0,
      numberOfVolunteers: 0,
      executiveTeams: this.buildExecutiveTeamPayload(),
      boardMembers: this.buildBoardMemberPayload(),
      partnerOrganisations: this.buildPartnerPayload(),
      budgetOrganisations: this.prepareBudgetPayload(),
      submitted: false,
      languageUuid: this.data.language.uuid,
      organisationDocuments: []
    };

    // Call the API
    this.organizationProjectService.createOrUpdateOrganisation(payload).subscribe({
      next: (response) => {
        // Save the organisation UUID for subsequent updates
        if (response.response?.uuid) {
          this.organisationUuid = response.response.uuid;
        }

        const isFirstSave = !this.hasBeenSavedOnce;
        this.hasBeenSavedOnce = true;
        this.isPreviewAvailable = true;

        if (isFirstSave) {
          this.toastService.success(this.translationService.instant('settings.charity_details_form.success_charity_added'));
        } else {
          this.toastService.success(this.translationService.instant('settings.charity_details_form.success_saved_as_draft'));
        }

        this.trackInProgressTabs((formName, formStep) => this.analyticsService.trackFormPartial(formName, formStep));

        // Trigger pending file input if upload was requested before first save
        if (this.pendingUploadFileInputId) {
          const fileInput = document.getElementById(this.pendingUploadFileInputId) as HTMLInputElement;
          this.pendingUploadFileInputId = null;
          if (fileInput) {
            fileInput.click();
          }
        }
        // Don't close the dialog - keep modal open for further editing
      },
      error: (error) => {
        console.error('Failed to save charity:', error);

        // Check if error is due to duplicate charity name
        if (error.error?.message?.includes('already exists') ||
            error.error?.message?.includes('already taken') ||
            error.error?.message === 'NAME_EXISTS') {
          this.charityNameError = this.translationService.instant('settings.charity_details_form.error_charity_name_taken');
          this.toastService.error(this.translationService.instant('settings.charity_details_form.error_charity_name_taken'));
          this.trackFieldError(FormStep.DETAILS, 'organizationName');
        } else {
          this.toastService.error(this.translationService.instant('settings.charity_details_form.error_failed_to_save'));
        }
      }
    });
  }

  /**
   * Submit form - validate and show confirmation dialog
   */
  onSubmit(): void {
    // Validate all required fields
    this.errorMessages = [];
    this.showErrorSummary = false;

    // Check charity name
    if (!this.detailsForm.get('basicInformation.organizationName')?.value ||
      this.detailsForm.get('basicInformation.organizationName')?.value.trim() === '') {
      this.errorMessages.push(this.translationService.instant('settings.charity_details_form.error_charity_name_required_submit'));
      this.trackFieldError(FormStep.DETAILS, 'organizationName');
    }

    // Check charity profile
    if (!this.detailsForm.get('basicInformation.charityProfile')?.value && !this.selectedProfileUuid) {
      this.errorMessages.push('Charity profile must be selected');
      this.trackFieldError(FormStep.DETAILS, 'charityProfile');
    }

    // Check mandatory documents
    if (this.isFieldRequired('organisationDocuments') && !this.hasValidDocumentsData()) {
      this.documentsNextAttempted = true;
      this.currentStep = FormStep.DOCUMENTS;
      this.trackFieldError(FormStep.DOCUMENTS, 'organisationDocuments');
      return;
    }

    // Check mandatory gallery
    if (this.isFieldRequired('organisationImages') && !this.hasValidGalleryData()) {
      this.galleryNextAttempted = true;
      this.currentStep = FormStep.GALLERY;
      this.trackFieldError(FormStep.GALLERY, 'organisationImages');
      return;
    }

    // Check mandatory financials
    if (!this.validateAllBudgetYears()) {
      this.currentStep = FormStep.FINANCIALS;
      this.toastService.error(this.translationService.instant('toast.validation_error'));
      return;
    }

    // Mark all fields as touched to show validation errors
    this.detailsForm.markAllAsTouched();
    this.markTeamFormTouched();
    this.cdr.detectChanges();

    // If there are validation errors, show error summary
    if (this.errorMessages.length > 0 || this.detailsForm.invalid || !this.isTeamFormValidForSubmit) {
      this.showErrorSummary = true;
      if (this.detailsForm.invalid || !this.isTeamFormValidForSubmit) {
        this.errorMessages.push(this.translationService.instant('settings.charity_details_form.error_fill_all_required_fields'));
      }
      if (this.detailsForm.invalid) {
        this.trackFieldErrors(FormStep.DETAILS, this.getInvalidDetailsFieldNames());
      }
      if (!this.isTeamFormValidForSubmit) {
        this.trackFieldErrors(FormStep.TEAM, this.getInvalidTeamFieldNames());
      }
      // Scroll to top to show error summary
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Check allowOrganizationApproval setting to decide whether to show confirmation dialog
    this.generalService.getGeneralSettings().pipe(take(1)).subscribe({
      next: (response) => {
        const configMap = response.response.globalConfig.reduce((acc, item) => {
          acc[item.key] = item.value;
          return acc;
        }, {} as Record<string, string>);

        const allowOrgApproval = configMap['allowOrganizationApproval'] === 'true';

        if (allowOrgApproval) {
          // Show confirmation dialog before submitting
          const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '512px',
            maxWidth: '90vw',
            data: {
              message: 'settings.charity_details_form.confirm_submission_message',
              actionType: 'restore',
              confirmButtonText: 'settings.charity_details_form.submit',
              cancelButtonText: 'settings.charity_details_form.cancel'
            }
          });

          dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (!confirmed) return;
            this.executeSubmission();
          });
        } else {
          // Skip dialog and submit directly
          this.executeSubmission();
        }
      },
      error: () => {
        // On settings fetch failure, fall back to showing the confirmation dialog
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
          width: '512px',
          maxWidth: '90vw',
          data: {
            message: 'settings.charity_details_form.confirm_submission_message',
            actionType: 'restore',
            confirmButtonText: 'settings.charity_details_form.submit',
            cancelButtonText: 'settings.charity_details_form.cancel'
          }
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
          if (!confirmed) return;
          this.executeSubmission();
        });
      }
    });
  }

  /**
   * Build and send the charity submission payload to the API.
   * Called after confirmation (dialog or direct) depending on allowOrganizationApproval setting.
   */
  private executeSubmission(): void {
    const formValue = this.getFlattenedFormValues();
    // Validate budget years - check for partial data
    if (!this.validateAllBudgetYears()) {
      this.currentStep = FormStep.FINANCIALS;
      this.toastService.error(this.translationService.instant('toast.validation_error'));
      return;
    }

    const teamFormValue = this.teamForm.value;

    // Build charity leader from current form values
    const charityLeader = this.buildCharityLeaderFromForm();

    // Build the request payload
    const payload: CreateUpdateOrganisationRequest = {
      organisationUuid: this.organisationUuid,
      mandatoryProfileUuid: formValue.charityProfile || this.selectedProfileUuid || '',
      organisationName: formValue.organizationName || '',
      isPrivate: formValue.private || false,
      address: {
        streetName: formValue.streetName || '',
        streetNumber: null,
        city: formValue.city || '',
        country: this.dropdowns?.countries.find(c => c.uuid === formValue.country)?.name || formValue.country || '',
        countryCode: null,
        latitude: null,
        longitude: null,
        stateProvince: formValue.stateProvince || '',
        postalCode: '',
        landmarkAddress: formValue.landmark || null
      },
      organisationWebsite: formValue.website || '',
      socialMediaLinkedin: formValue.linkedin || null,
      socialMediaTwitter: formValue.twitter || null,
      socialMediaFacebook: formValue.facebook || null,
      socialMediaInstagram: formValue.instagram || null,
      cause: formValue.cause?.[0] ? this.dropdowns?.causes.find(c => c.uuid === formValue.cause[0]) || null : null,
      organisationCategories: formValue.category?.map((uuid: string) =>
        this.dropdowns?.categories.find(c => c.uuid === uuid)
      ).filter((item: any) => item !== undefined) || [],
      targetPopulation: formValue.targetPopulation || null,
      organisationInfluencedCountries: formValue.influencedCountries?.map((uuid: string) =>
        this.dropdowns?.countries.find(c => c.uuid === uuid)
      ).filter((item: any) => item !== undefined) || [],
      globalInfluence: this.isGlobalInfluenceSelected,
      regionOrder: this.buildRegionOrderPayload(),
      yearFounded: formValue.yearFounded || null,
      givingLink: this.buildGivingLinkPayloadValue(formValue.givingLink),
      organisationVideo: null,
      organisationImages: this.buildOrganisationImagesPayload(),
      executiveSummary: formValue.executiveSummary || '',
      visionStatement: formValue.visionStatement || null,
      missionStatement: formValue.missionStatement || null,
      organisationValues: null,
      programAndActivities: '',
      history: formValue.history || null,
      organisationLeader: charityLeader,
      modifiedFields: {},
      numberOfEmployees: parseInt(teamFormValue.numberOfTeamMembers) || 0,
      numberOfVolunteers: 0,
      executiveTeams: this.buildExecutiveTeamPayload(),
      boardMembers: this.buildBoardMemberPayload(),
      partnerOrganisations: this.buildPartnerPayload(),
      budgetOrganisations: this.prepareBudgetPayload(),
      submitted: true,
      languageUuid: this.data.language.uuid,
      organisationDocuments: []
    };

    // Call the API
    this.organizationProjectService.createOrUpdateOrganisation(payload).subscribe({
      next: (response) => {
        // Save the organisation UUID for subsequent updates
        if (response.response?.uuid) {
          this.organisationUuid = response.response.uuid;
        }
        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_charity_submitted'));
        this.trackAllTabsCompleteOnSubmit();
        this.dialogRef.close({ action: 'submit', data: response });
      },
      error: (error) => {
        console.error('Failed to submit charity:', error);
        if (error.error?.message === 'NAME_EXISTS') {
          this.toastService.error(this.translationService.instant('settings.charity_details_form.error_charity_name_taken'));
          this.trackFieldError(FormStep.DETAILS, 'organizationName');
        } else {
          this.toastService.error(this.translationService.instant('settings.charity_details_form.error_failed_to_submit'));
        }
      }
    });
  }

  /**
   * Close dialog
   */
  onCancel(): void {
    this.trackInProgressTabs((formName, formStep) => this.analyticsService.trackFormAbandon(formName, formStep));
    this.dialogRef.close(null);
  }

  /**
   * Toggle countries dropdown
   */
  toggleCountriesDropdown(): void {
    if (this.isCountriesDropdownOpen) {
      this.closeCountriesDropdown();
    } else {
      this.isCountriesDropdownOpen = true;
    }
  }

  /**
   * Whether the synthetic "Global" option is selected, i.e. every available
   * country is currently selected. Derived so individual country toggles keep
   * Global in sync automatically (unchecking any country unchecks Global;
   * selecting the last country re-checks it).
   */
  get isGlobalInfluenceSelected(): boolean {
    const total = this.dropdowns?.countries?.length || 0;
    return total > 0 && this.selectedCountries.length === total;
  }

  /**
   * Toggle the "Global" option: select all countries when not all are selected,
   * otherwise clear the selection.
   */
  toggleGlobalInfluence(): void {
    if (this.isGlobalInfluenceSelected) {
      this.selectedCountries = [];
    } else {
      this.selectedCountries = (this.dropdowns?.countries || []).map(c => c.uuid);
    }
    this.updateInfluencedCountriesDisplay();
    this.scheduleCountryChipsUpdate();
  }

  /**
   * Remove the "Global" chip: clears all selected countries.
   */
  removeGlobalInfluenceChip(event: Event): void {
    this.clearAllCountries(event);
  }

  /**
   * Toggle country selection
   */
  toggleCountrySelection(countryUuid: string): void {
    const index = this.selectedCountries.indexOf(countryUuid);
    if (index > -1) {
      this.selectedCountries.splice(index, 1);
    } else {
      this.selectedCountries.push(countryUuid);
    }
    this.updateInfluencedCountriesDisplay();
    this.scheduleCountryChipsUpdate();
  }

  /**
   * Check if country is selected
   */
  isCountrySelected(countryUuid: string): boolean {
    return this.selectedCountries.includes(countryUuid);
  }

  /**
   * Update influenced countries display
   */
  updateInfluencedCountriesDisplay(): void {
    this.detailsForm.patchValue({
      charityDetails: {
        influencedCountries: this.selectedCountries
      }
    });
    this.reconcileRegionsFromCountries();
  }

  /**
   * Derive the region field from the current country selection.
   * A region is "valid" when at least one selected country is listed in its
   * `countriesUuids`. Regions no longer backed by any selected country are
   * dropped; newly-valid regions are appended (in dropdown order) without
   * disturbing the existing (possibly drag-reordered) sequence, and a region
   * associated with multiple selected countries is never added twice.
   */
  reconcileRegionsFromCountries(): void {
    const regions = this.dropdowns?.regions || [];

    // When Global influence is active, collapse all regions to the single "Global" region.
    if (this.isGlobalInfluenceSelected) {
      const globalRegion = regions.find(r => r.name?.toLowerCase() === 'global');
      this.selectedRegions = globalRegion ? [globalRegion.uuid] : [];
      return;
    }

    const validRegionUuids = new Set<string>();
    for (const region of regions) {
      const countryUuids = region.countriesUuids || [];
      if (countryUuids.some(cu => this.selectedCountries.includes(cu))) {
        validRegionUuids.add(region.uuid);
      }
    }
    // Remove orphaned regions, preserving the order of those that remain.
    this.selectedRegions = this.selectedRegions.filter(uuid => validRegionUuids.has(uuid));
    // Append newly-associated regions not already present.
    for (const region of regions) {
      if (validRegionUuids.has(region.uuid) && !this.selectedRegions.includes(region.uuid)) {
        this.selectedRegions.push(region.uuid);
      }
    }
  }

  /**
   * Ordered region items for display, following the `selectedRegions` sequence.
   */
  getSelectedRegionChips(): RegionDropdownItem[] {
    const regions = this.dropdowns?.regions || [];
    return this.selectedRegions
      .map(uuid => regions.find(r => r.uuid === uuid))
      .filter((r): r is RegionDropdownItem => r !== undefined);
  }

  /**
   * Reorder region chips via drag-and-drop.
   */
  onRegionDrop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.selectedRegions, event.previousIndex, event.currentIndex);
  }

  /**
   * Build the `regionOrder` payload: one { uuid, position } per selected region,
   * with a 1-based position matching the current drag order.
   */
  private buildRegionOrderPayload(): RegionOrderItem[] {
    return this.selectedRegions.map((uuid, index) => ({ uuid, position: index + 1 }));
  }

  /**
   * Get selected countries display text
   */
  getSelectedCountriesText(): string {
    if (!this.dropdowns || this.selectedCountries.length === 0) {
      return '';
    }
    const countryNames = this.selectedCountries
      .map(uuid => this.dropdowns!.countries.find(c => c.uuid === uuid)?.name)
      .filter(name => name !== undefined);
    return countryNames.join(', ');
  }

  /**
   * Close countries dropdown when clicking outside
   */
  closeCountriesDropdown(): void {
    this.isCountriesDropdownOpen = false;
    this.countrySearchText = '';
    this.detailsForm.get('charityDetails.influencedCountries')?.markAsTouched();
  }

  /**
   * Get filtered countries based on search
   */
  getFilteredCountries() {
    if (!this.dropdowns?.countries) return [];
    if (!this.countrySearchText.trim()) return this.dropdowns.countries;

    const searchLower = this.countrySearchText.toLowerCase();
    return this.dropdowns.countries.filter(country =>
      country.name.toLowerCase().includes(searchLower)
    );
  }

  /**
   * Remove a country chip
   */
  removeCountryChip(countryUuid: string, event: Event): void {
    event.stopPropagation();
    this.toggleCountrySelection(countryUuid);
  }

  /**
   * Get selected country chips
   */
  getSelectedCountryChips() {
    if (!this.dropdowns) return [];
    return this.selectedCountries
      .map(uuid => this.dropdowns!.countries.find(c => c.uuid === uuid))
      .filter(country => country !== undefined);
  }

  /**
   * Get visible country chips (limited to first line)
   */
  getVisibleCountryChips() {
    const chips = this.getSelectedCountryChips();
    return chips.slice(0, this.maxVisibleCountryChips);
  }

  /**
   * Get remaining country chips count
   */
  getRemainingCountryChipsCount(): number {
    const totalChips = this.getSelectedCountryChips().length;
    return Math.max(0, totalChips - this.maxVisibleCountryChips);
  }

  /**
   * Check if there are more country chips than currently visible
   */
  hasMoreCountryChips(): boolean {
    return this.getRemainingCountryChipsCount() > 0;
  }

  /**
   * Clear all countries
   */
  clearAllCountries(event: Event): void {
    event.stopPropagation();
    this.selectedCountries = [];
    this.maxVisibleCountryChips = 999;
    this.updateInfluencedCountriesDisplay();
  }

  /**
   * Schedule a DOM-based recalculation of how many country chips fit in the input.
   */
  private scheduleCountryChipsUpdate(): void {
    this.maxVisibleCountryChips = this.selectedCountries.length || 999;
    setTimeout(() => this.computeCountryChipsLimit());
  }

  /**
   * Measure rendered chip widths and compute the max number that fit
   * in the country chips container, reserving space for a "+n more" badge when needed.
   */
  private computeCountryChipsLimit(): void {
    if (!this.countryChipsContainerEl) return;
    const container = this.countryChipsContainerEl.nativeElement;
    const containerWidth = container.clientWidth;
    if (containerWidth === 0) return;

    const GAP = 6;
    const MORE_BADGE_WIDTH = 78;
    const chipEls = Array.from(
      container.querySelectorAll<HTMLElement>('.charity-form__chip:not(.charity-form__chip--more)')
    );

    if (chipEls.length === 0) return;

    const totalWidth = chipEls.reduce(
      (sum, el, i) => sum + el.offsetWidth + (i > 0 ? GAP : 0), 0
    );

    if (totalWidth <= containerWidth) {
      this.maxVisibleCountryChips = chipEls.length;
      return;
    }

    let available = containerWidth - MORE_BADGE_WIDTH - GAP;
    let count = 0;
    for (let i = 0; i < chipEls.length; i++) {
      const chipWidth = chipEls[i].offsetWidth + (i > 0 ? GAP : 0);
      if (available >= chipWidth) {
        available -= chipWidth;
        count++;
      } else {
        break;
      }
    }

    this.maxVisibleCountryChips = Math.max(1, count);
    this.cdr.markForCheck();
  }

  /**
   * Toggle categories dropdown
   */
  toggleCategoriesDropdown(): void {
    if (this.isCategoriesDropdownOpen) {
      this.closeCategoriesDropdown();
    } else {
      this.isCategoriesDropdownOpen = true;
    }
  }

  /**
   * Toggle category selection with max limit
   */
  toggleCategorySelection(categoryUuid: string): void {
    const index = this.selectedCategories.indexOf(categoryUuid);

    if (index > -1) {
      // Allow deselection always
      this.selectedCategories.splice(index, 1);
    } else {
      // Only allow selection if below max limit
      if (this.selectedCategories.length < this.maxCategoriesLimit) {
        this.selectedCategories.push(categoryUuid);
      }
    }

    this.updateCategoryDisplay();
    this.scheduleCategoryChipsUpdate();
  }

  /**
   * Check if category is selected
   */
  isCategorySelected(categoryUuid: string): boolean {
    return this.selectedCategories.includes(categoryUuid);
  }

  /**
   * Check if category selection is disabled (max reached)
   */
  isCategorySelectionDisabled(categoryUuid: string): boolean {
    // Allow if already selected (for deselection) or if below max limit
    return !this.isCategorySelected(categoryUuid) &&
           this.selectedCategories.length >= this.maxCategoriesLimit;
  }

  /**
   * Update category display
   */
  updateCategoryDisplay(): void {
    this.detailsForm.patchValue({
      charityDetails: {
        category: this.selectedCategories
      }
    });
  }

  /**
   * Get selected categories display text
   */
  getSelectedCategoriesText(): string {
    if (!this.dropdowns || this.selectedCategories.length === 0) {
      return '';
    }
    const categoryNames = this.selectedCategories
      .map(uuid => this.dropdowns!.categories.find(c => c.uuid === uuid)?.name)
      .filter(name => name !== undefined);
    return categoryNames.join(', ');
  }

  /**
   * Get category selection count text
   */
  getCategoryCountText(): string {
    return `${this.selectedCategories.length}/${this.maxCategoriesLimit} selected`;
  }

  /**
   * Close categories dropdown when clicking outside
   */
  closeCategoriesDropdown(): void {
    this.isCategoriesDropdownOpen = false;
    this.categorySearchText = '';
    this.detailsForm.get('charityDetails.category')?.markAsTouched();
  }

  /**
   * Get filtered categories based on search
   */
  getFilteredCategories() {
    if (!this.dropdowns?.categories) return [];
    if (!this.categorySearchText.trim()) return this.dropdowns.categories;

    const searchLower = this.categorySearchText.toLowerCase();
    return this.dropdowns.categories.filter(cat =>
      cat.name.toLowerCase().includes(searchLower)
    );
  }

  /**
   * Remove a category chip
   */
  removeCategoryChip(categoryUuid: string, event: Event): void {
    event.stopPropagation();
    this.toggleCategorySelection(categoryUuid);
  }

  /**
   * Get selected category chips
   */
  getSelectedCategoryChips() {
    if (!this.dropdowns) return [];
    return this.selectedCategories
      .map(uuid => this.dropdowns!.categories.find(c => c.uuid === uuid))
      .filter(cat => cat !== undefined);
  }

  /**
   * Get visible category chips (limited to first line)
   */
  getVisibleCategoryChips() {
    const chips = this.getSelectedCategoryChips();
    return chips.slice(0, this.maxVisibleCategoryChips);
  }

  /**
   * Get remaining category chips count
   */
  getRemainingCategoryChipsCount(): number {
    const totalChips = this.getSelectedCategoryChips().length;
    return Math.max(0, totalChips - this.maxVisibleCategoryChips);
  }

  /**
   * Check if there are more category chips than currently visible
   */
  hasMoreCategoryChips(): boolean {
    return this.getRemainingCategoryChipsCount() > 0;
  }

  /**
   * Schedule a DOM-based recalculation of how many category chips fit in the input.
   * Sets maxVisibleCategoryChips to total first (so all chips render), then measures
   * actual widths after the DOM updates.
   */
  private scheduleCategoryChipsUpdate(): void {
    this.maxVisibleCategoryChips = this.selectedCategories.length || 999;
    setTimeout(() => this.computeCategoryChipsLimit());
  }

  /**
   * Measure rendered chip widths and compute the max number that fit
   * in the chips container, reserving space for a "+n more" badge when needed.
   */
  private computeCategoryChipsLimit(): void {
    if (!this.categoryChipsContainerEl) return;
    const container = this.categoryChipsContainerEl.nativeElement;
    const containerWidth = container.clientWidth;
    if (containerWidth === 0) return;

    const GAP = 6;
    const MORE_BADGE_WIDTH = 78; // approx width of "+n more" chip
    const chipEls = Array.from(
      container.querySelectorAll<HTMLElement>('.charity-form__chip:not(.charity-form__chip--more)')
    );

    if (chipEls.length === 0) return;

    // Sum widths of all chips
    const totalWidth = chipEls.reduce(
      (sum, el, i) => sum + el.offsetWidth + (i > 0 ? GAP : 0), 0
    );

    if (totalWidth <= containerWidth) {
      // All chips fit — no truncation needed
      this.maxVisibleCategoryChips = chipEls.length;
      return;
    }

    // Find how many chips fit while reserving space for the "+n more" badge
    let available = containerWidth - MORE_BADGE_WIDTH - GAP;
    let count = 0;
    for (let i = 0; i < chipEls.length; i++) {
      const chipWidth = chipEls[i].offsetWidth + (i > 0 ? GAP : 0);
      if (available >= chipWidth) {
        available -= chipWidth;
        count++;
      } else {
        break;
      }
    }

    this.maxVisibleCategoryChips = Math.max(1, count);
    this.cdr.markForCheck();
  }

  /**
   * Clear all categories
   */
  clearAllCategories(event: Event): void {
    event.stopPropagation();
    this.selectedCategories = [];
    this.maxVisibleCategoryChips = 999;
    this.updateCategoryDisplay();
  }

  // ─────────────────────────────────────────────────
  // Cause multi-select methods
  // ─────────────────────────────────────────────────

  toggleCausesDropdown(): void {
    if (this.isCausesDropdownOpen) {
      this.closeCausesDropdown();
    } else {
      this.isCausesDropdownOpen = true;
    }
  }

  toggleCauseSelection(causeUuid: string): void {
    this.selectedCause = this.selectedCause === causeUuid ? null : causeUuid;
    this.updateCauseDisplay();
    this.closeCausesDropdown();
  }

  isCauseSelected(causeUuid: string): boolean {
    return this.selectedCause === causeUuid;
  }

  updateCauseDisplay(): void {
    this.detailsForm.patchValue({
      charityDetails: {
        cause: this.selectedCause ? [this.selectedCause] : []
      }
    });
  }

  closeCausesDropdown(): void {
    this.isCausesDropdownOpen = false;
    this.causeSearchText = '';
    this.detailsForm.get('charityDetails.cause')?.markAsTouched();
  }

  getFilteredCauses() {
    if (!this.dropdowns?.causes) return [];
    if (!this.causeSearchText.trim()) return this.dropdowns.causes;
    const searchLower = this.causeSearchText.toLowerCase();
    return this.dropdowns.causes.filter(c => c.name.toLowerCase().includes(searchLower));
  }

  getSelectedCauseName(): string | null {
    if (!this.selectedCause || !this.dropdowns) return null;
    return this.dropdowns.causes.find(c => c.uuid === this.selectedCause)?.name || null;
  }

  clearSelectedCause(event: Event): void {
    event.stopPropagation();
    this.selectedCause = null;
    this.updateCauseDisplay();
  }

  clearAllCauses(event: Event): void {
    this.clearSelectedCause(event);
  }

  /**
   * Handle charity profile selection change
   */
  onCharityProfileChange(event: any): void {
    const selectedUuid = this.detailsForm.get('basicInformation.charityProfile')?.value;
    this.selectedProfileUuid = selectedUuid;

    const selectedProfile = this.profiles.find(p => p.uuid === selectedUuid);

    if (selectedUuid) {
      this.loadProfileFieldConfiguration(selectedUuid);
    }
  }

  /**
   * Flatten the schema from getAllProfileFields into a path-keyed map.
   * Mirrors the same helper in charity-profile-setup-dialog.
   */
  private flattenSchemaFields(fields: { [key: string]: any }, parentKey?: string): { [key: string]: any } {
    return Object.entries(fields).reduce((acc: { [key: string]: any }, [key, field]) => {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      acc[fullKey] = field;
      if (field.name && !(field.name in acc)) {
        acc[field.name] = field;
      }
      if (field.innerFields) {
        Object.assign(acc, this.flattenSchemaFields(field.innerFields, fullKey));
      }
      return acc;
    }, {});
  }

  /**
   * Build env-specific uuid → config.name map using the schema.
   * config.name is unique per field and stable across environments.
   */
  private buildUuidToConfigName(schemaFlat: { [key: string]: any }): Map<string, string> {
    const map = new Map<string, string>();
    CHARITY_FORM_CONFIG.forEach(fieldConfig => {
      if (!fieldConfig.key) return;
      const schemaGroupPath = fieldConfig.group
        ? (SCHEMA_GROUP_PATH[fieldConfig.group as FormGroupName] ?? fieldConfig.group)
        : null;
      const groupPath = schemaGroupPath ? `${schemaGroupPath}.${fieldConfig.key}` : null;
      const schemaField = (groupPath && schemaFlat[groupPath]) || schemaFlat[fieldConfig.key];
      if (schemaField?.uuid) {
        map.set(schemaField.uuid, fieldConfig.name);
      }
    });
    return map;
  }

  /**
   * Load profile field configuration from API.
   * Fetches the schema once and caches it; only re-fetches the profile on each call.
   */
  private loadProfileFieldConfiguration(profileUuid: string): void {
    const schema$ = this.schemaFlat
      ? of(this.schemaFlat)
      : this.charityProfileService.getAllProfileFields().pipe(
          map((response: AllProfileFieldsResponse) => {
            this.schemaFlat = this.flattenSchemaFields(response.data);
            return this.schemaFlat;
          }),
          catchError(() => of({} as { [key: string]: any }))
        );

    forkJoin({
      schema: schema$,
      profile: this.charityProfileService.getProfileFieldsById(profileUuid)
    }).subscribe({
      next: ({ schema, profile }) => {
        this.applyProfileFieldConfiguration(profile, schema);
      },
      error: (error) => {
        console.error('Error loading profile configuration:', error);
      }
    });
  }

  /**
   * Apply profile field configuration to form.
   * Uses env-specific UUID → config.name map derived from the schema so that
   * ambiguous field names (e.g. 'email' in four team sections) are resolved
   * correctly regardless of which environment the UUIDs come from.
   */
  private applyProfileFieldConfiguration(
    response: ProfileFieldsResponse,
    schemaFlat: { [key: string]: any }
  ): void {
    if (!response.data || !response.data.innerFields) return;

    this.fieldVisibility.clear();

    // env-specific uuid → unique config.name (e.g. 'leaderEmail', 'executiveEmail')
    const uuidToConfigName = this.buildUuidToConfigName(schemaFlat);

    // Recursively walk all innerFields (handles financial fields nested under budgetOrganisations)
    const walk = (fields: { [key: string]: any }) => {
      Object.values(fields).forEach((apiField: any) => {
        if (!apiField) return;

        const visibility: 'optional' | 'mandatory' | 'hidden' =
          apiField.hidden === true ? 'hidden' :
          apiField.hidden === false ? (apiField.mandatory === false ? 'optional' : 'mandatory') :
          'optional';

        // Store under unique config.name when UUID is recognised; fall back to plain name
        const storageKey = (apiField.uuid && uuidToConfigName.has(apiField.uuid))
          ? uuidToConfigName.get(apiField.uuid)!
          : apiField.name;

        this.fieldVisibility.set(storageKey, visibility);

        // Recurse into nested innerFields (e.g. budgetOrganisations → budgetYear, budgetRevenues → itemName)
        if (apiField.innerFields && typeof apiField.innerFields === 'object') {
          walk(apiField.innerFields);
        }
      });
    };

    walk(response.data.innerFields);

    this.applyVisibilityToForms();
    this.refreshVisibleSteps();
  }

  /**
   * Apply visibility rules to form controls
   */
  private applyVisibilityToForms(): void {
    // These groups back the "add new" input cards (not yet committed entries).
    // Required validators must not be applied to them — validation for those
    // inputs is handled inline when the user commits an entry.
    const addNewGroups: FormGroupName[] = ['executiveTeam', 'boardMembers', 'partnerOrganizations'];

    CHARITY_FORM_CONFIG.forEach(fieldConfig => {
      if (!fieldConfig.key) return;
      if (addNewGroups.includes(fieldConfig.group)) return;
      // File-upload fields hold their state outside the form (uploadedCoverImage /
      // uploadedLogo), so a Validators.required on their always-empty form control
      // would permanently invalidate detailsForm. Required-ness is enforced via
      // isSubmitDisabled and onNext against the uploaded* state instead.
      if (SHADOW_FILE_UPLOAD_FIELDS.has(fieldConfig.name)) return;

      const visibility = this.getFieldVisibility(fieldConfig);

      // Find the control in the form
      let control = null;

      // Check in details form groups
      ['basicInformation', 'address', 'socialMediaLinks', 'charityDetails'].forEach(groupName => {
        const group = this.detailsForm.get(groupName);
        if (group) {
          const fieldControl = group.get(fieldConfig.name);
          if (fieldControl) {
            control = fieldControl;
          }
        }
      });

      // Check in team form
      if (!control) {
        control = this.teamForm.get(fieldConfig.name);
      }

      if (control) {
        // Apply validators based on visibility
        const baseValidators = fieldConfig.validators || [];
        if (visibility === 'mandatory') {
          // Add required validator if not already present
          control.setValidators(
            baseValidators.includes(Validators.required)
              ? baseValidators
              : [Validators.required, ...baseValidators]
          );
        } else if (visibility === 'optional' || visibility === 'hidden') {
          // Strip required so a missing/empty value can't block submit.
          // Hidden fields are also removed from the template; any other
          // validators (maxLength, email, …) still apply if a value exists.
          const nonRequired = baseValidators.filter(v => v !== Validators.required);
          control.setValidators(nonRequired.length > 0 ? nonRequired : null);
        } else {
          // No opinion from the profile — reset to config defaults so a
          // previously-applied required validator from another profile
          // doesn't linger after the user switches profiles.
          control.setValidators(baseValidators.length > 0 ? baseValidators : null);
        }
        // emitEvent:false — this only recalculates validity from a validator
        // change, the value itself hasn't changed. Without this, every field's
        // valueChanges fires whenever profile field configuration (re)loads
        // (on init and on every profile switch), which the per-tab form_start
        // tracking (see ngOnInit) would otherwise mistake for a genuine user
        // edit — prematurely marking a tab "started" before the user has
        // touched it, and permanently swallowing their real first keystroke.
        control.updateValueAndValidity({ emitEvent: false });
      }
    });

    this.applyValidatorsToExistingMemberGroups();
  }

  private applyValidatorsToExistingMemberGroups(): void {
    if (!this.fieldVisibility.size) return;

    const applyToGroup = (fg: FormGroup, fieldMap: { control: string; configName: string }[]) => {
      fieldMap.forEach(({ control, configName }) => {
        const formControl = fg.get(control);
        if (!formControl) return;
        const fieldConfig = CHARITY_FORM_CONFIG.find(c => c.name === configName);
        if (!fieldConfig) return;
        const base = fieldConfig.validators || [];
        const visibility = this.getFieldVisibility(fieldConfig);
        if (visibility === 'mandatory') {
          formControl.setValidators(base.includes(Validators.required) ? base : [Validators.required, ...base]);
        } else if (visibility === 'optional' || visibility === 'hidden') {
          const nonRequired = base.filter((v: any) => v !== Validators.required);
          formControl.setValidators(nonRequired.length > 0 ? nonRequired : null);
        } else {
          // No opinion from the profile — reset to config defaults so a
          // previously-applied required validator doesn't linger after a
          // profile switch.
          formControl.setValidators(base.length > 0 ? base : null);
        }
        formControl.updateValueAndValidity({ emitEvent: false });
      });
    };

    const executiveMap = [
      { control: 'fullName', configName: 'executiveName' },
      { control: 'email', configName: 'executiveEmail' },
      { control: 'bio', configName: 'executiveBio' },
      { control: 'linkedinProfile', configName: 'executiveLinkedin' },
    ];
    this.existingExecutiveFormGroups.forEach(fg => applyToGroup(fg, executiveMap));

    const boardMap = [
      { control: 'fullName', configName: 'boardMemberName' },
      { control: 'email', configName: 'boardMemberEmail' },
      { control: 'bio', configName: 'boardMemberBio' },
      { control: 'linkedinProfile', configName: 'boardMemberLinkedin' },
    ];
    this.existingBoardMemberFormGroups.forEach(fg => applyToGroup(fg, boardMap));

    const partnerMap = [
      { control: 'name', configName: 'partnerName' },
      { control: 'email', configName: 'partnerEmail' },
      { control: 'relationshipDescription', configName: 'partnerDescription' },
    ];
    this.existingPartnerFormGroups.forEach(fg => applyToGroup(fg, partnerMap));
  }

  /**
   * Submit button disabled state. Combines form validity with the upload-state
   * checks for shadow file-upload fields (coverPhoto, logo), whose values live
   * outside the form so they can't be enforced via Validators.required.
   */
  get isSubmitDisabled(): boolean {
    if (this.detailsForm?.invalid || !this.isTeamFormValidForSubmit) return true;
    if (this.isFieldRequired('coverPhoto') && !this.uploadedCoverImage) return true;
    if (this.isFieldRequired('logo') && !this.uploadedLogo) return true;
    return false;
  }

  /**
   * True when every committed team member/partner form group and every
   * charityLeader/teamInformation control is valid. "Add new" input card
   * controls (executiveTeam, boardMembers, partnerOrganizations groups in
   * teamForm) are excluded — they are in-progress entries not yet committed.
   */
  get isTeamFormValidForSubmit(): boolean {
    const addNewGroups: FormGroupName[] = ['executiveTeam', 'boardMembers', 'partnerOrganizations'];
    const teamFormValid = CHARITY_FORM_CONFIG
      .filter(f => f.tab === 'team' && !addNewGroups.includes(f.group))
      .every(f => {
        const control = this.teamForm?.get(f.name);
        return !control || control.valid;
      });
    if (!teamFormValid) return false;

    return [
      ...this.existingExecutiveFormGroups,
      ...this.existingBoardMemberFormGroups,
      ...this.existingPartnerFormGroups,
    ].every(fg => fg.valid);
  }

  private markTeamFormTouched(): void {
    const addNewGroups: FormGroupName[] = ['executiveTeam', 'boardMembers', 'partnerOrganizations'];
    CHARITY_FORM_CONFIG
      .filter(f => f.tab === 'team' && !addNewGroups.includes(f.group))
      .forEach(f => this.teamForm.get(f.name)?.markAsTouched());
    [...this.existingExecutiveFormGroups, ...this.existingBoardMemberFormGroups, ...this.existingPartnerFormGroups]
      .forEach(fg => fg.markAllAsTouched());
  }

  // Returns visibility for a specific field config.
  // fieldVisibility is keyed by config.name (unique per field, env-agnostic).
  private getFieldVisibility(fieldConfig: { name?: string; key?: string }): 'optional' | 'mandatory' | 'hidden' | undefined {
    if (fieldConfig.name) {
      const byName = this.fieldVisibility.get(fieldConfig.name);
      if (byName !== undefined) return byName;
    }
    return fieldConfig.key ? this.fieldVisibility.get(fieldConfig.key) : undefined;
  }

  /**
   * Check if a field should be visible
   */
  isFieldVisible(fieldName: string): boolean {
    const fieldConfig = CHARITY_FORM_CONFIG.find(config => config.name === fieldName);
    if (!fieldConfig || !fieldConfig.key) return true;

    const visibility = this.getFieldVisibility(fieldConfig);
    if (!visibility) return true;

    return visibility !== 'hidden';
  }

  isSectionVisible(groupName: FormGroupName): boolean {
    return getFieldsByGroup(groupName).some(field => this.isFieldVisible(field.name));
  }

  isTabVisible(tab: FormTab): boolean {
    const fields = CHARITY_FORM_CONFIG.filter(f => f.tab === tab && f.labelKey);
    if (fields.length === 0) return true;
    return fields.some(field => {
      if (!field.key) return true;
      // fieldVisibility is keyed by config.name (unique, env-agnostic); fall back to key
      const visibility = this.fieldVisibility.get(field.name) ?? this.fieldVisibility.get(field.key);
      if (visibility === undefined) return true; // absent = optional = visible
      return visibility !== 'hidden';
    });
  }

  private refreshVisibleSteps(): void {
    const allStepDefs: Array<{ step: FormStep; tab: FormTab; labelKey: string }> = [
      { step: FormStep.DETAILS, tab: 'details', labelKey: 'settings.charity_details_form.step_details' },
      { step: FormStep.TEAM, tab: 'team', labelKey: 'settings.charity_details_form.step_team' },
      { step: FormStep.FINANCIALS, tab: 'financials', labelKey: 'settings.charity_details_form.step_financials' },
      { step: FormStep.DOCUMENTS, tab: 'documents', labelKey: 'settings.charity_details_form.step_documents' },
      { step: FormStep.GALLERY, tab: 'gallery', labelKey: 'settings.charity_details_form.step_gallery' },
    ];

    this.steps = allStepDefs
      .filter(s => this.isTabVisible(s.tab))
      .map((s, index) => ({
        step: s.step,
        label: this.translationService.instant(s.labelKey),
        number: index + 1
      }));

    if (!this.steps.find(s => s.step === this.currentStep)) {
      this.currentStep = this.steps[0]?.step ?? FormStep.DETAILS;
    }
  }

  private getNextVisibleStep(): FormStep | null {
    const visibleSteps = this.steps.map(s => s.step).sort((a, b) => a - b);
    const idx = visibleSteps.indexOf(this.currentStep);
    return idx >= 0 && idx < visibleSteps.length - 1 ? visibleSteps[idx + 1] : null;
  }

  private getPreviousVisibleStep(): FormStep | null {
    const visibleSteps = this.steps.map(s => s.step).sort((a, b) => a - b);
    const idx = visibleSteps.indexOf(this.currentStep);
    return idx > 0 ? visibleSteps[idx - 1] : null;
  }

  /**
   * Check if a field is required
   */
  isFieldRequired(fieldName: string): boolean {
    const fieldConfig = CHARITY_FORM_CONFIG.find(config => config.name === fieldName);
    if (!fieldConfig || !fieldConfig.key) {
      // Check original validators as fallback
      return fieldConfig?.validators?.includes(Validators.required) || false;
    }

    const visibility = this.getFieldVisibility(fieldConfig);
    return visibility === 'mandatory';
  }

  /**
   * Check if a financials field is required
   * TODO: Once financials fields are added to CHARITY_FORM_CONFIG and integrated with
   * the dynamic profile configuration, this method will use the fieldVisibility map.
   * For now, it uses a hardcoded list of required fields.
   *
   * @param fieldKey - The database key for the financials field (e.g., 'budgetYear', 'fiscalYearEnd', 'budgetCurrency')
   */
  protected isFinancialsFieldRequired(fieldKey: string): boolean {
    const visibility = this.fieldVisibility.get(fieldKey);
    return visibility === 'mandatory';
  }

  protected isItemStarted(item: BudgetItem): boolean {
    return !!(item.itemName?.trim() || item.budget != null || item.actual != null);
  }

  protected isFinancialsSectionRequired(section: 'revenue' | 'expenses'): boolean {
    const revenueFields = ['revenueItemName', 'revenueBudget', 'revenueActuals', 'totalBudgetRevenues', 'totalActualsRevenues'];
    const expensesFields = ['expenseItemName', 'expenseBudget', 'expenseActuals', 'totalBudgetExpenses', 'totalActualsExpenses'];
    const fields = section === 'revenue' ? revenueFields : expensesFields;
    return fields.some(field => this.isFinancialsFieldRequired(field));
  }

  protected isAnyFinancialsFieldRequired(): boolean {
    const allFields = [
      'budgetYear', 'fiscalYearEnd', 'budgetCurrency',
      'revenueItemName', 'revenueBudget', 'revenueActuals',
      'expenseItemName', 'expenseBudget', 'expenseActuals',
      'totalBudgetRevenues', 'totalActualsRevenues',
      'totalBudgetExpenses', 'totalActualsExpenses',
      'netGainLossBudget', 'netGainLossActuals',
    ];
    return allFields.some(field => this.isFinancialsFieldRequired(field));
  }

  /**
   * Save charity leader
   */
  saveCharityLeader(): void {
    const formValue = this.teamForm.value;

    if (!formValue.leaderName || !formValue.leaderEmail) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_leader_required_fields'));
      return;
    }

    const leaderUuid = this.uploadedLeaderPicture?.objectUuid || this.charityLeader?.uuid;

    this.charityLeader = {
      ...(leaderUuid && { uuid: leaderUuid }),
      position: 0,
      fullName: formValue.leaderName,
      profilePicture: {
        fileType: 'image',
        allowedFileType: 'image',
        cover: false,
        pendingUpload: false,
        isFormatValid: true,
        progress: 0,
        ...(this.uploadedLeaderPicture?.fileUuid && { uuid: this.uploadedLeaderPicture.fileUuid }),
        ...(this.uploadedLeaderPicture?.fileUrl && { url: this.uploadedLeaderPicture.fileUrl }),
        ...(this.uploadedLeaderPicture?.fileName && { fileName: this.uploadedLeaderPicture.fileName }),
      },
      bio: formValue.leaderBio || '',
      linkedinProfile: formValue.leaderLinkedin || '',
      email: formValue.leaderEmail
    };

    this.toastService.success(this.translationService.instant('settings.charity_details_form.success_leader_saved'));
  }

  /**
   * Add executive team member
   */
  addExecutiveTeamMember(): void {
    const formValue = this.teamForm.value;

    if (!formValue.executiveName || !formValue.executiveEmail) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_name_email_required'));
      return;
    }

    const bio = formValue.executiveBio || '';

    const teamMember: OrganisationTeamMember = {
      ...(this.uploadedExecutivePicture?.objectUuid && { uuid: this.uploadedExecutivePicture.objectUuid }),
      position: this.editingExecutiveIndex !== null ? this.editingExecutiveIndex : this.executiveTeamMembers.length,
      fullName: formValue.executiveName,
      profilePicture: {
        fileType: 'image',
        allowedFileType: 'image',
        cover: false,
        pendingUpload: false,
        isFormatValid: true,
        progress: 0,
        ...(this.uploadedExecutivePicture && {
          url: this.uploadedExecutivePicture.fileUrl,
          uuid: this.uploadedExecutivePicture.fileUuid,
          fileName: this.uploadedExecutivePicture.fileName,
          objectUuid: this.uploadedExecutivePicture.objectUuid
        })
      },
      bio: bio.trim(),
      linkedinProfile: formValue.executiveLinkedin || '',
      email: formValue.executiveEmail
    };

    if (this.editingExecutiveIndex !== null) {
      this.executiveTeamMembers[this.editingExecutiveIndex] = teamMember;
      this.existingExecutiveFormGroups[this.editingExecutiveIndex] = this.buildExecutiveMemberFormGroup(teamMember);
      this.editingExecutiveIndex = null;
    } else {
      this.executiveTeamMembers.push(teamMember);
      this.existingExecutiveFormGroups.push(this.buildExecutiveMemberFormGroup(teamMember));
    }
    this.applyValidatorsToExistingMemberGroups();

    this.uploadedExecutivePicture = null;

    // Reset form
    this.teamForm.patchValue({
      executiveName: '',
      executiveTitle: '',
      executiveEmail: '',
      executiveLinkedin: '',
      executiveBio: ''
    });
  }

  /**
   * Edit executive team member
   */
  editExecutiveTeamMember(index: number): void {
    const member = this.executiveTeamMembers[index];
    this.editingExecutiveIndex = index;

    this.teamForm.patchValue({
      executiveName: member.fullName,
      executiveEmail: member.email,
      executiveLinkedin: member.linkedinProfile,
      executiveBio: member.bio
    });

    this.uploadedExecutivePicture = member.profilePicture?.url
      ? { fileName: member.profilePicture.fileName || '', fileUrl: member.profilePicture.url, fileUuid: member.profilePicture.uuid || '', objectUuid: member.profilePicture.objectUuid }
      : null;
  }

  /**
   * Delete executive team member
   */
  deleteExecutiveTeamMember(index: number): void {
    this.executiveTeamMembers.splice(index, 1);
    this.existingExecutiveFormGroups.splice(index, 1);
    // Reset positions
    this.executiveTeamMembers.forEach((member, i) => {
      member.position = i;
    });
  }

  /**
   * Add board member
   */
  addBoardMember(): void {
    const formValue = this.teamForm.value;

    if (!formValue.boardMemberName || !formValue.boardMemberEmail) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_name_email_required'));
      return;
    }

    const bio = formValue.boardMemberBio || '';

    const boardMember: OrganisationTeamMember = {
      ...(this.uploadedBoardMemberPicture?.objectUuid && { uuid: this.uploadedBoardMemberPicture.objectUuid }),
      position: this.editingBoardMemberIndex !== null ? this.editingBoardMemberIndex : this.boardMembers.length,
      fullName: formValue.boardMemberName,
      profilePicture: {
        fileType: 'image',
        allowedFileType: 'image',
        cover: false,
        pendingUpload: false,
        isFormatValid: true,
        progress: 0,
        ...(this.uploadedBoardMemberPicture && {
          url: this.uploadedBoardMemberPicture.fileUrl,
          uuid: this.uploadedBoardMemberPicture.fileUuid,
          fileName: this.uploadedBoardMemberPicture.fileName,
          objectUuid: this.uploadedBoardMemberPicture.objectUuid
        })
      },
      bio: bio.trim(),
      linkedinProfile: formValue.boardMemberLinkedin || '',
      email: formValue.boardMemberEmail
    };

    if (this.editingBoardMemberIndex !== null) {
      this.boardMembers[this.editingBoardMemberIndex] = boardMember;
      this.existingBoardMemberFormGroups[this.editingBoardMemberIndex] = this.buildBoardMemberFormGroup(boardMember);
      this.editingBoardMemberIndex = null;
    } else {
      this.boardMembers.push(boardMember);
      this.existingBoardMemberFormGroups.push(this.buildBoardMemberFormGroup(boardMember));
    }
    this.applyValidatorsToExistingMemberGroups();

    this.uploadedBoardMemberPicture = null;

    // Reset form
    this.teamForm.patchValue({
      boardMemberName: '',
      boardMemberTitle: '',
      boardMemberEmail: '',
      boardMemberLinkedin: '',
      boardMemberBio: ''
    });
  }

  /**
   * Edit board member
   */
  editBoardMember(index: number): void {
    const member = this.boardMembers[index];
    this.editingBoardMemberIndex = index;

    this.teamForm.patchValue({
      boardMemberName: member.fullName,
      boardMemberEmail: member.email,
      boardMemberLinkedin: member.linkedinProfile,
      boardMemberBio: member.bio
    });

    this.uploadedBoardMemberPicture = member.profilePicture?.url
      ? { fileName: member.profilePicture.fileName || '', fileUrl: member.profilePicture.url, fileUuid: member.profilePicture.uuid || '', objectUuid: member.profilePicture.objectUuid }
      : null;
  }

  /**
   * Delete board member
   */
  deleteBoardMember(index: number): void {
    this.boardMembers.splice(index, 1);
    this.existingBoardMemberFormGroups.splice(index, 1);
    // Reset positions
    this.boardMembers.forEach((member, i) => {
      member.position = i;
    });
  }

  /**
   * Add partner organization
   */
  addPartnerOrganization(): void {
    const formValue = this.teamForm.value;

    if (!formValue.partnerName || !formValue.partnerEmail) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_partner_name_email_required'));
      return;
    }

    const partner: OrganisationPartner = {
      position: this.partnerOrganizations.length,
      name: formValue.partnerName,
      relationshipDescription: formValue.partnerDescription || '',
      email: formValue.partnerEmail
    };

    this.partnerOrganizations.push(partner);
    this.existingPartnerFormGroups.push(this.buildPartnerFormGroup(partner));
    this.applyValidatorsToExistingMemberGroups();

    this.teamForm.patchValue({
      partnerName: '',
      partnerEmail: '',
      partnerDescription: ''
    });
  }

  /**
   * Delete partner organization
   */
  deletePartnerOrganization(index: number): void {
    this.partnerOrganizations.splice(index, 1);
    this.existingPartnerFormGroups.splice(index, 1);
    this.partnerOrganizations.forEach((partner, i) => {
      partner.position = i;
    });
  }

  getExistingPartnerFormGroup(index: number): FormGroup {
    return this.existingPartnerFormGroups[index];
  }

  /**
   * Updates an existing partner organization field in place and mirrors the
   * change into its FormGroup control, so isTeamFormValidForSubmit validates
   * the current value rather than the stale one captured when the group was built.
   */
  updatePartnerField(index: number, field: 'name' | 'email' | 'relationshipDescription', value: string): void {
    const partner = this.partnerOrganizations[index];
    if (!partner) return;
    partner[field] = value;
    this.existingPartnerFormGroups[index]?.get(field)?.setValue(value);
  }

  /**
   * Get initials from name for avatar placeholder
   */
  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  /**
   * Get character count for a form field
   */
  getCharacterCount(fieldName: string): string {
    const value = this.detailsForm.get(`charityDetails.${fieldName}`)?.value || '';
    return `${value.length}/1000`;
  }

  /**
   * Open preview (placeholder for future implementation)
   */
  onPreview(): void {
    // TODO: Implement preview functionality
    this.toastService.success(this.translationService.instant('settings.charity_details_form.info_preview_coming_soon'));
  }

  /**
   * Trigger file input for logo upload
   */
  onUploadLogoClick(): void {
    if (!this.organisationUuid) {
      this.pendingUploadFileInputId = 'logoFileInput';
      this.onSaveAsDraft();
      return;
    }
    const fileInput = document.getElementById('logoFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * Trigger file input for cover image upload
   */
  onUploadCoverImageClick(): void {
    if (!this.organisationUuid) {
      this.pendingUploadFileInputId = 'coverImageFileInput';
      this.onSaveAsDraft();
      return;
    }
    const fileInput = document.getElementById('coverImageFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * Handle logo file selection
   */
  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/bmp', 'image/png', 'image/jfif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_invalid_image_type'));
      input.value = ''; // Reset input
      return;
    }

    // Upload the file
    this.uploadLogo(file);
    input.value = ''; // Reset input for future uploads
  }

  onCoverImageFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/bmp', 'image/png', 'image/jfif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_invalid_image_type'));
      input.value = '';
      return;
    }

    this.pendingCoverImageFileName = file.name;
    input.value = '';

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      this.coverImageCropper.open(dataUrl);
    };
    reader.readAsDataURL(file);
  }

  onCoverImageCroppedBlob(blob: Blob): void {
    const file = new File([blob], this.pendingCoverImageFileName || 'cover-image.jpg', { type: blob.type });
    this.uploadCoverImage(file);
  }

  /**
   * Upload logo file
   */
  private uploadLogo(file: File): void {
    this.isUploadingLogo = true;

    const params = {
      entityUuid: this.organisationUuid || '',
      fileType: 'ORGANISATION_LOGO',
      secondUuid: '',
      file: file
    };

    this.generalService.uploadFile(params).subscribe({
      next: (response: FileUploadResponse) => {
        this.isUploadingLogo = false;

        // If organisationUuid was null and response has objectUuid, set it
        if (!this.organisationUuid && (response as any).objectUuid) {
          this.organisationUuid = (response as any).objectUuid;
        }

        // Store uploaded file details
        this.uploadedLogo = {
          fileName: this.extractFileName(response.response.fileName || file.name),
          fileUrl: response.response.url,
          fileUuid: response.response.uuid
        };

        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_logo_uploaded'));
      },
      error: (error) => {
        console.error('Failed to upload logo:', error);
        this.isUploadingLogo = false;
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_logo_upload_failed'));
      }
    });
  }

  /**
   * Upload cover image file
   */
  private uploadCoverImage(file: File): void {
    this.isUploadingCoverImage = true;

    const params = {
      entityUuid: this.organisationUuid || '',
      fileType: 'ORGANISATION_COVER_IMAGE',
      secondUuid: '',
      file: file
    };

    this.generalService.uploadFile(params).subscribe({
      next: (response: FileUploadResponse) => {
        this.isUploadingCoverImage = false;

        // Set organisationUuid from objectUuid in the upload response
        const objectUuid = (response as any).objectUuid ?? (response as any).response?.objectUuid;
        if (objectUuid) {
          this.organisationUuid = objectUuid;
        }

        // Store uploaded file details
        this.uploadedCoverImage = {
          fileName: this.extractFileName(response.response.fileName || file.name),
          fileUrl: response.response.url,
          fileUuid: response.response.uuid
        };
        this.coverPhotoError = false;

        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_cover_image_uploaded'));
      },
      error: (error) => {
        console.error('Failed to upload cover image:', error);
        this.isUploadingCoverImage = false;
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_cover_image_upload_failed'));
      }
    });
  }

  /**
   * Preview logo in new tab
   */
  previewLogo(): void {
    if (this.uploadedLogo?.fileUrl) {
      this.dialog.open(MediaPreviewDialogComponent, {
        data: {
          url: this.uploadedLogo.fileUrl,
          fileName: this.uploadedLogo.fileName,
          fileType: this.getImageFileType(this.uploadedLogo.fileName)
        },
        panelClass: 'media-preview-dialog',
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '100vw',
        height: '100vh',
      });
    }
  }

  /**
   * Preview cover image in a modal
   */
  previewCoverImage(): void {
    if (this.uploadedCoverImage?.fileUrl) {
      this.dialog.open(MediaPreviewDialogComponent, {
        data: {
          url: this.uploadedCoverImage.fileUrl,
          fileName: this.uploadedCoverImage.fileName,
          fileType: this.getImageFileType(this.uploadedCoverImage.fileName)
        },
        panelClass: 'media-preview-dialog',
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '100vw',
        height: '100vh',
      });
    }
  }

  /**
   * Delete logo file
   */
  deleteLogo(): void {
    if (!this.uploadedLogo || !this.organisationUuid) {
      return;
    }

    const params = {
      entityUuid: this.organisationUuid,
      fileType: 'ORGANISATION_LOGO',
      fileUuid: this.uploadedLogo.fileUuid
    };

    this.generalService.deleteFile(params).subscribe({
      next: () => {
        this.uploadedLogo = null;
        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_logo_deleted'));
      },
      error: (error) => {
        console.error('Failed to delete logo:', error);
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_logo_delete_failed'));
      }
    });
  }

  /**
   * Delete cover image file
   */
  deleteCoverImage(): void {
    if (!this.uploadedCoverImage || !this.organisationUuid) {
      return;
    }

    const params = {
      entityUuid: this.organisationUuid,
      fileType: 'ORGANISATION_COVER_IMAGE',
      fileUuid: this.uploadedCoverImage.fileUuid
    };

    this.generalService.deleteFile(params).subscribe({
      next: () => {
        this.uploadedCoverImage = null;
        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_cover_image_deleted'));
      },
      error: (error) => {
        console.error('Failed to delete cover image:', error);
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_cover_image_delete_failed'));
      }
    });
  }

  // ==================== Team Tab Upload Methods ====================

  /**
   * Trigger file input for leader picture upload
   */
  onUploadLeaderPictureClick(): void {
    const fileInput = document.getElementById('leaderPictureFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * Handle leader picture file selection
   */
  onLeaderPictureFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    // Validate file type - images only
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/bmp', 'image/png', 'image/jfif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_invalid_image_type'));
      input.value = '';
      return;
    }

    // Upload the file
    this.uploadLeaderPicture(file);
    input.value = '';
  }

  /**
   * Upload leader picture file
   */
  private uploadLeaderPicture(file: File): void {
    if (!this.organisationUuid) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_save_charity_first'));
      return;
    }

    this.isUploadingLeaderPicture = true;

    const params = {
      entityUuid: this.organisationUuid,
      fileType: 'ORGANISATION_LEADER_PICTURE',
      secondUuid: '',
      file: file
    };

    this.generalService.uploadFile(params).subscribe({
      next: (response: FileUploadResponse) => {
        this.isUploadingLeaderPicture = false;
        console.log('[uploadLeaderPicture] full response:', JSON.stringify(response));

        const objectUuid = (response as any).objectUuid ?? (response as any).response?.objectUuid;
        this.uploadedLeaderPicture = {
          fileName: response.response.fileName || file.name,
          fileUrl: response.response.url,
          fileUuid: response.response.uuid,
          objectUuid
        };
        console.log('[uploadLeaderPicture] stored objectUuid:', objectUuid);

        if (objectUuid && this.charityLeader) {
          this.charityLeader = { ...this.charityLeader, uuid: objectUuid };
        }

        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_leader_picture_uploaded'));
      },
      error: (error) => {
        console.error('Failed to upload leader picture:', error);
        this.isUploadingLeaderPicture = false;
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_leader_picture_upload_failed'));
      }
    });
  }

  /**
   * Preview leader picture in a modal
   */
  previewLeaderPicture(): void {
    if (this.uploadedLeaderPicture?.fileUrl) {
      this.dialog.open(MediaPreviewDialogComponent, {
        data: {
          url: this.uploadedLeaderPicture.fileUrl,
          fileName: this.uploadedLeaderPicture.fileName,
          fileType: this.getImageFileType(this.uploadedLeaderPicture.fileName)
        },
        panelClass: 'media-preview-dialog',
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '100vw',
        height: '100vh',
      });
    }
  }

  /**
   * Preview a team member photo in a modal
   */
  previewMemberPhoto(picture: OrganisationMediaDto): void {
    if (picture?.url) {
      const fileType = (picture.fileType?.includes('/') ? picture.fileType : null)
        || this.getImageFileType(picture.fileName || '');
      this.dialog.open(MediaPreviewDialogComponent, {
        data: {
          url: picture.url,
          fileName: picture.fileName || '',
          fileType
        },
        panelClass: 'media-preview-dialog',
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '100vw',
        height: '100vh',
      });
    }
  }

  private getImageFileType(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const map: { [key: string]: string } = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
      bmp: 'image/bmp', webp: 'image/webp', jfif: 'image/jpeg', gif: 'image/gif'
    };
    return map[ext] || 'image/jpeg';
  }

  /**
   * Delete leader picture file
   */
  deleteLeaderPicture(): void {
    if (!this.uploadedLeaderPicture) {
      return;
    }

    const entityUuid = this.uploadedLeaderPicture.objectUuid || this.charityLeader?.uuid || '';
    const params = {
      entityUuid,
      fileType: 'ORGANISATION_LEADER_PICTURE',
      fileUuid: this.uploadedLeaderPicture.fileUuid
    };

    this.generalService.deleteFile(params).subscribe({
      next: () => {
        this.uploadedLeaderPicture = null;
        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_leader_picture_deleted'));
      },
      error: (error) => {
        console.error('Failed to delete leader picture:', error);
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_leader_picture_delete_failed'));
      }
    });
  }

  /**
   * Trigger file input for executive team picture upload
   */
  onUploadExecutivePictureClick(): void {
    const fileInput = document.getElementById('executivePictureFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onExecutivePhotoFile(file: File): void {
    this.uploadExecutivePicture(file);
  }

  getExistingExecutiveMemberForm(i: number): FormGroup {
    return this.existingExecutiveFormGroups[i];
  }

  onExistingExecutivePhotoFile(index: number, file: File): void {
    this.uploadExistingExecutivePicture(file, index);
  }

  private buildExecutiveMemberFormGroup(member: OrganisationTeamMember): FormGroup {
    return this.fb.group({
      fullName: [member.fullName || ''],
      email: [(member.email || '').trim()],
      bio: [member.bio || ''],
      linkedinProfile: [member.linkedinProfile || '']
    });
  }

  private buildExecutiveTeamPayload(): OrganisationTeamMember[] {
    return this.executiveTeamMembers.map((member, i) => ({
      ...member,
      fullName: this.existingExecutiveFormGroups[i]?.get('fullName')?.value ?? member.fullName,
      email: this.existingExecutiveFormGroups[i]?.get('email')?.value ?? member.email,
      bio: this.existingExecutiveFormGroups[i]?.get('bio')?.value ?? member.bio,
      linkedinProfile: this.existingExecutiveFormGroups[i]?.get('linkedinProfile')?.value ?? member.linkedinProfile,
    }));
  }

  getExistingBoardMemberForm(i: number): FormGroup {
    return this.existingBoardMemberFormGroups[i];
  }

  onExistingBoardMemberPhotoFile(index: number, file: File): void {
    this.uploadExistingBoardMemberPicture(file, index);
  }

  private buildBoardMemberFormGroup(member: OrganisationTeamMember): FormGroup {
    return this.fb.group({
      fullName: [member.fullName || ''],
      email: [(member.email || '').trim()],
      bio: [member.bio || ''],
      linkedinProfile: [member.linkedinProfile || '']
    });
  }

  private buildBoardMemberPayload(): OrganisationTeamMember[] {
    return this.boardMembers.map((member, i) => ({
      ...member,
      fullName: this.existingBoardMemberFormGroups[i]?.get('fullName')?.value ?? member.fullName,
      email: this.existingBoardMemberFormGroups[i]?.get('email')?.value ?? member.email,
      bio: this.existingBoardMemberFormGroups[i]?.get('bio')?.value ?? member.bio,
      linkedinProfile: this.existingBoardMemberFormGroups[i]?.get('linkedinProfile')?.value ?? member.linkedinProfile,
    }));
  }

  private buildPartnerFormGroup(partner: OrganisationPartner): FormGroup {
    return this.fb.group({
      name: [partner.name || ''],
      email: [(partner.email || '').trim()],
      relationshipDescription: [partner.relationshipDescription || '']
    });
  }

  private buildPartnerPayload(): OrganisationPartner[] {
    return this.partnerOrganizations.map(partner => ({ ...partner }));
  }

  /**
   * Handle executive team picture file selection
   */
  onExecutivePictureFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    // Validate file type - images only
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/bmp', 'image/png', 'image/jfif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_invalid_image_type'));
      input.value = '';
      return;
    }

    // Upload the file
    this.uploadExecutivePicture(file);
    input.value = '';
  }

  /**
   * Upload executive team picture file
   */
  private uploadExecutivePicture(file: File): void {
    if (!this.organisationUuid) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_save_charity_first'));
      return;
    }

    this.isUploadingExecutivePicture = true;

    const params = {
      entityUuid: this.organisationUuid,
      fileType: 'EXECUTIVE_TEAM_PICTURE',
      secondUuid: '',
      file: file
    };

    this.generalService.uploadFile(params).subscribe({
      next: (response: FileUploadResponse) => {
        this.isUploadingExecutivePicture = false;

        const objectUuid = (response as any).objectUuid ?? (response as any).response?.objectUuid;
        this.uploadedExecutivePicture = {
          fileName: response.response.fileName || file.name,
          fileUrl: response.response.url,
          fileUuid: response.response.uuid,
          objectUuid
        };

        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_executive_picture_uploaded'));
      },
      error: (error) => {
        console.error('Failed to upload executive picture:', error);
        this.isUploadingExecutivePicture = false;
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_executive_picture_upload_failed'));
      }
    });
  }

  /**
   * Preview executive picture in a modal
   */
  previewExecutivePicture(): void {
    if (this.uploadedExecutivePicture?.fileUrl) {
      this.dialog.open(MediaPreviewDialogComponent, {
        data: {
          url: this.uploadedExecutivePicture.fileUrl,
          fileName: this.uploadedExecutivePicture.fileName,
          fileType: this.getImageFileType(this.uploadedExecutivePicture.fileName)
        },
        panelClass: 'media-preview-dialog',
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '100vw',
        height: '100vh',
      });
    }
  }

  /**
   * Delete executive picture file
   */
  deleteExecutivePicture(): void {
    if (!this.uploadedExecutivePicture) {
      return;
    }

    const params = {
      entityUuid: this.uploadedExecutivePicture.objectUuid || '',
      fileType: 'EXECUTIVE_TEAM_PICTURE',
      fileUuid: this.uploadedExecutivePicture.fileUuid
    };

    this.generalService.deleteFile(params).subscribe({
      next: () => {
        this.uploadedExecutivePicture = null;
        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_executive_picture_deleted'));
      },
      error: (error) => {
        console.error('Failed to delete executive picture:', error);
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_executive_picture_delete_failed'));
      }
    });
  }

  onUploadExistingExecutivePictureClick(index: number): void {
    this.uploadingExistingExecutiveIndex = index;
    const fileInput = document.getElementById('existingExecutivePictureFileInput') as HTMLInputElement;
    if (fileInput) fileInput.click();
  }

  onExistingExecutivePictureFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/bmp', 'image/png', 'image/jfif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_invalid_image_type'));
      input.value = '';
      return;
    }
    if (this.uploadingExistingExecutiveIndex === null) return;
    this.uploadExistingExecutivePicture(file, this.uploadingExistingExecutiveIndex);
    input.value = '';
  }

  private uploadExistingExecutivePicture(file: File, index: number): void {

    if (!this.organisationUuid) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_save_charity_first'));
      return;
    }
    const member = this.executiveTeamMembers[index];
    const params = {
      entityUuid: this.organisationUuid,
      fileType: 'EXECUTIVE_TEAM_PICTURE',
      secondUuid: member.uuid || '',
      file: file
    };
    this.generalService.uploadFile(params).subscribe({
      next: (response: FileUploadResponse) => {
        const objectUuid = (response as any).objectUuid ?? (response as any).response?.objectUuid;
        this.executiveTeamMembers[index] = {
          ...this.executiveTeamMembers[index],
          profilePicture: {
            ...this.executiveTeamMembers[index].profilePicture,
            url: response.response.url,
            uuid: response.response.uuid,
            fileName: response.response.fileName || file.name,
            objectUuid
          }
        };

        if (index !== null && !this.executiveTeamMembers[index].uuid && objectUuid) {
          this.executiveTeamMembers[index].uuid = objectUuid;
        }

        this.uploadingExistingExecutiveIndex = null;
        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_executive_picture_uploaded'));
      },
      error: (error) => {
        console.error('Failed to upload executive picture:', error);
        this.uploadingExistingExecutiveIndex = null;
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_executive_picture_upload_failed'));
      }
    });
  }

  deleteExistingExecutivePicture(index: number): void {
    const member = this.executiveTeamMembers[index];
    if (!member?.profilePicture?.uuid) {
      member.profilePicture = { ...member.profilePicture, url: undefined, uuid: undefined, fileName: undefined };
      return;
    }
    const params = {
      entityUuid: member.profilePicture.objectUuid || member.uuid || '',
      fileType: 'EXECUTIVE_TEAM_PICTURE',
      fileUuid: member.profilePicture.uuid
    };
    this.generalService.deleteFile(params).subscribe({
      next: () => {
        this.executiveTeamMembers[index] = {
          ...this.executiveTeamMembers[index],
          profilePicture: { ...this.executiveTeamMembers[index].profilePicture, url: undefined, uuid: undefined, fileName: undefined }
        };
        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_executive_picture_deleted'));
      },
      error: (error) => {
        console.error('Failed to delete executive picture:', error);
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_executive_picture_delete_failed'));
      }
    });
  }

  /**
   * Trigger file input for board member picture upload
   */
  onUploadBoardMemberPictureClick(): void {
    const fileInput = document.getElementById('boardMemberPictureFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * Handle board member picture file selection
   */
  onBoardMemberPhotoFile(file: File): void {
    this.uploadBoardMemberPicture(file);
  }

  onBoardMemberPictureFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    // Validate file type - images only
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/bmp', 'image/png', 'image/jfif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_invalid_image_type'));
      input.value = '';
      return;
    }

    // Upload the file
    this.uploadBoardMemberPicture(file);
    input.value = '';
  }

  /**
   * Upload board member picture file
   */
  private uploadBoardMemberPicture(file: File): void {
    if (!this.organisationUuid) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_save_charity_first'));
      return;
    }

    this.isUploadingBoardMemberPicture = true;

    const params = {
      entityUuid: this.organisationUuid,
      fileType: 'BOARD_MEMBER_PICTURE',
      secondUuid: '',
      file: file
    };

    this.generalService.uploadFile(params).subscribe({
      next: (response: FileUploadResponse) => {
        this.isUploadingBoardMemberPicture = false;

        const objectUuid = (response as any).objectUuid ?? (response as any).response?.objectUuid;
        this.uploadedBoardMemberPicture = {
          fileName: response.response.fileName || file.name,
          fileUrl: response.response.url,
          fileUuid: response.response.uuid,
          objectUuid
        };

        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_board_member_picture_uploaded'));
      },
      error: (error) => {
        console.error('Failed to upload board member picture:', error);
        this.isUploadingBoardMemberPicture = false;
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_board_member_picture_upload_failed'));
      }
    });
  }

  /**
   * Preview board member picture in a modal
   */
  previewBoardMemberPicture(): void {
    if (this.uploadedBoardMemberPicture?.fileUrl) {
      this.dialog.open(MediaPreviewDialogComponent, {
        data: {
          url: this.uploadedBoardMemberPicture.fileUrl,
          fileName: this.uploadedBoardMemberPicture.fileName,
          fileType: this.getImageFileType(this.uploadedBoardMemberPicture.fileName)
        },
        panelClass: 'media-preview-dialog',
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '100vw',
        height: '100vh',
      });
    }
  }

  /**
   * Delete board member picture file
   */
  deleteBoardMemberPicture(): void {
    if (!this.uploadedBoardMemberPicture) {
      return;
    }

    const params = {
      entityUuid: this.uploadedBoardMemberPicture.objectUuid || '',
      fileType: 'BOARD_MEMBER_PICTURE',
      fileUuid: this.uploadedBoardMemberPicture.fileUuid
    };

    this.generalService.deleteFile(params).subscribe({
      next: () => {
        this.uploadedBoardMemberPicture = null;
        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_board_member_picture_deleted'));
      },
      error: (error) => {
        console.error('Failed to delete board member picture:', error);
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_board_member_picture_delete_failed'));
      }
    });
  }

  onUploadExistingBoardMemberPictureClick(index: number): void {
    this.uploadingExistingBoardMemberIndex = index;
    const fileInput = document.getElementById('existingBoardMemberPictureFileInput') as HTMLInputElement;
    if (fileInput) fileInput.click();
  }

  onExistingBoardMemberPictureFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/bmp', 'image/png', 'image/jfif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_invalid_image_type'));
      input.value = '';
      return;
    }
    if (this.uploadingExistingBoardMemberIndex === null) return;
    this.uploadExistingBoardMemberPicture(file, this.uploadingExistingBoardMemberIndex);
    input.value = '';
  }

  private uploadExistingBoardMemberPicture(file: File, index: number): void {
    if (!this.organisationUuid) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.error_save_charity_first'));
      return;
    }
    const member = this.boardMembers[index];
    const params = {
      entityUuid: this.organisationUuid,
      fileType: 'BOARD_MEMBER_PICTURE',
      secondUuid: member.uuid || '',
      file: file
    };
    this.generalService.uploadFile(params).subscribe({
      next: (response: FileUploadResponse) => {
        const objectUuid = (response as any).objectUuid ?? (response as any).response?.objectUuid;
        this.boardMembers[index] = {
          ...this.boardMembers[index],
          profilePicture: {
            ...this.boardMembers[index].profilePicture,
            url: response.response.url,
            uuid: response.response.uuid,
            fileName: response.response.fileName || file.name,
            objectUuid
          }
        };

        if (index !== null && !this.boardMembers[index].uuid && objectUuid) {
          this.boardMembers[index].uuid = objectUuid;
        }

        this.uploadingExistingBoardMemberIndex = null;
        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_board_member_picture_uploaded'));
      },
      error: (error) => {
        console.error('Failed to upload board member picture:', error);
        this.uploadingExistingBoardMemberIndex = null;
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_board_member_picture_upload_failed'));
      }
    });
  }

  deleteExistingBoardMemberPicture(index: number): void {
    const member = this.boardMembers[index];
    if (!member?.profilePicture?.uuid) {
      member.profilePicture = { ...member.profilePicture, url: undefined, uuid: undefined, fileName: undefined };
      return;
    }
    const params = {
      entityUuid: member.profilePicture.objectUuid || member.uuid || '',
      fileType: 'BOARD_MEMBER_PICTURE',
      fileUuid: member.profilePicture.uuid
    };
    this.generalService.deleteFile(params).subscribe({
      next: () => {
        this.boardMembers[index] = {
          ...this.boardMembers[index],
          profilePicture: { ...this.boardMembers[index].profilePicture, url: undefined, uuid: undefined, fileName: undefined }
        };
        this.toastService.success(this.translationService.instant('settings.charity_details_form.success_board_member_picture_deleted'));
      },
      error: (error) => {
        console.error('Failed to delete board member picture:', error);
        this.toastService.error(this.translationService.instant('settings.charity_details_form.error_board_member_picture_delete_failed'));
      }
    });
  }

  // ==================== Documents Tab Methods ====================

  /**
   * Handle document file selection
   */
  onDocumentFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.trackTabStart(FormStep.DOCUMENTS);
      this.uploadDocument(input.files[0]);
      // Clear the input value to allow uploading the same file again
      input.value = '';
    }
  }

  /**
   * Handle drag over event for document upload area
   */
  onDocumentDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Handle drop event for document upload area
   */
  onDocumentDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files && event.dataTransfer.files[0]) {
      this.trackTabStart(FormStep.DOCUMENTS);
      this.uploadDocument(event.dataTransfer.files[0]);
    }
  }

  /**
   * Upload document file
   * API: POST api/v1/general/upload/file
   * Form data:
   *   - entityUuid: UUID of org/charity (can be retrieved after saving as draft)
   *   - fileType: ORGANISATION_DOCUMENT
   *   - secondUuid: blank/none
   *   - file: (binary) the file
   * Accepted formats: txt, pdf, doc, docx, xls, xlsx, ppt, pptx
   * Response: { response: { uuid: string, url: string, type: string } }
   */
  private uploadDocument(file: File): void {
    // Check file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!this.SUPPORTED_DOCUMENT_TYPES.includes(file.type) &&
        !(fileExtension && this.SUPPORTED_DOCUMENT_EXTENSIONS.includes(fileExtension))) {
      const errorMessage = this.translationService.instant('settings.charity_details_form.documents.error_file_type_not_supported');
      this.uploadedDocuments.push({
        fileName: file.name,
        fileUrl: '',
        fileUuid: '',
        fileSize: file.size,
        fileSizeFormatted: this.formatFileSize(file.size),
        status: 'error',
        errorMessage
      });
      this.trackFieldError(FormStep.DOCUMENTS, 'organisationDocuments');
      return;
    }

    // Check for duplicate files - extract filename from path for comparison
    const isDuplicate = this.uploadedDocuments.some(doc => {
      const existingFileName = this.extractFilename(doc.fileName);
      return existingFileName === file.name && doc.status === 'success';
    });
    if (isDuplicate) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.documents.error_file_already_uploaded'));
      return;
    }

    // Upload file via API
    this.isUploadingDocument = true;

    const params = {
      entityUuid: this.organisationUuid || '',
      fileType: 'ORGANISATION_DOCUMENT',
      secondUuid: '',
      file: file
    };

    this.generalService.uploadFile(params).subscribe({
      next: (response: FileUploadResponse) => {
        this.isUploadingDocument = false;

        // If organisationUuid was null and response has objectUuid, set it
        if (!this.organisationUuid && (response as any).objectUuid) {
          this.organisationUuid = (response as any).objectUuid;
        }

        // Store uploaded document details
        this.uploadedDocuments.push({
          fileName: response.response.fileName || file.name,
          fileUrl: response.response.url,
          fileUuid: response.response.uuid,
          fileSize: file.size,
          fileSizeFormatted: this.formatFileSize(file.size),
          status: 'success'
        });

        this.toastService.success(this.translationService.instant('settings.charity_details_form.documents.success_document_uploaded'));
      },
      error: (error) => {
        console.error('Failed to upload document:', error);
        this.isUploadingDocument = false;
        const errorMessage = this.translationService.instant('error.file_upload_failed');
        this.toastService.error(errorMessage);
        this.uploadedDocuments.push({
          fileName: file.name,
          fileUrl: '',
          fileUuid: '',
          fileSize: file.size,
          fileSizeFormatted: this.formatFileSize(file.size),
          status: 'error',
          errorMessage
        });
        this.trackFieldError(FormStep.DOCUMENTS, 'organisationDocuments');
      }
    });
  }

  /**
   * Delete a document
   * API: DELETE api/v1/general/delete/file?entityUuid={organisationUuid}&fileType=ORGANISATION_DOCUMENT&fileUuid={fileUuid}
   * Query params:
   *   - entityUuid: Organisation UUID
   *   - fileType: ORGANISATION_DOCUMENT
   *   - fileUuid: UUID of the file (from upload response.response.uuid)
   */
  deleteDocument(index: number): void {
    const document = this.uploadedDocuments[index];

    // If it's a failed upload (no UUID), just remove from the list locally
    if (!document.fileUuid) {
      this.uploadedDocuments.splice(index, 1);
      return;
    }

    // Delete file via API
    const params = {
      entityUuid: this.organisationUuid || '',
      fileType: 'ORGANISATION_DOCUMENT',
      fileUuid: document.fileUuid
    };

    this.generalService.deleteFile(params).subscribe({
      next: () => {
        this.uploadedDocuments.splice(index, 1);
        this.toastService.success(this.translationService.instant('settings.charity_details_form.documents.success_document_deleted'));
      },
      error: (error) => {
        console.error('Failed to delete document:', error);
        const errorMessage = this.translationService.instant('settings.charity_details_form.documents.error_document_delete_failed');
        this.toastService.error(errorMessage);
        this.uploadedDocuments[index].status = 'error';
        this.uploadedDocuments[index].errorMessage = errorMessage;
      }
    });
  }

  /**
   * Preview a document
   * Opens the document in a new tab for preview
   * Follows the same pattern as charity profile documents view
   */
  previewDocument(document: any): void {
    if (document.status === 'error') {
      return;
    }

    if (document.fileUrl) {
      window.open(document.fileUrl, '_blank', 'noopener,noreferrer');
    }
  }

  /**
   * Format file size for display
   */
  private extractFileName(path: string): string {
    return path.split('/').pop() || path;
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  // ==================== Gallery Tab Methods ====================

  /**
   * Handle gallery media file selection
   */
  onGalleryMediaFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.trackTabStart(FormStep.GALLERY);
      this.uploadGalleryMedia(input.files[0]);
      // Clear the input value to allow uploading the same file again
      input.value = '';
    }
  }

  /**
   * Handle drag over event for gallery upload area
   */
  onGalleryMediaDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Handle drop event for gallery upload area
   */
  onGalleryMediaDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files && event.dataTransfer.files[0]) {
      this.trackTabStart(FormStep.GALLERY);
      this.uploadGalleryMedia(event.dataTransfer.files[0]);
    }
  }

  /**
   * Upload gallery media file (image or video)
   * API: POST api/v1/general/upload/file
   * Form data:
   *   - entityUuid: UUID of org/charity
   *   - fileType: ORGANISATION_IMAGE
   *   - secondUuid: blank/none
   *   - file: (binary) the file
   * Accepted formats: images (jpg, png, bmp, svg, webp) and videos (mp4, mov, mpeg, ogg, avi, webm, 3gp)
   */
  private uploadGalleryMedia(file: File): void {
    // Check file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!this.SUPPORTED_GALLERY_TYPES.includes(file.type) &&
        !(fileExtension && this.SUPPORTED_GALLERY_EXTENSIONS.includes(fileExtension))) {
      const errorMessage = this.translationService.instant('settings.charity_details_form.gallery.error_file_type_not_supported');
      this.uploadedGalleryMedia.push({
        fileName: file.name,
        fileUrl: '',
        fileUuid: '',
        fileSize: file.size,
        fileSizeFormatted: this.formatFileSize(file.size),
        fileType: file.type,
        status: 'error',
        errorMessage
      });
      this.trackFieldError(FormStep.GALLERY, 'organisationImages');
      return;
    }

    // Check video limit: only 1 video allowed per organization
    const isVideoFile = file.type.startsWith('video/');
    if (isVideoFile) {
      const hasExistingVideo = this.uploadedGalleryMedia.some(media =>
        this.isVideo(media.fileType) && media.status === 'success'
      );
      if (hasExistingVideo) {
        this.toastService.error(this.translationService.instant('settings.charity_details_form.gallery.error_video_limit_reached'));
        return;
      }
    }

    // Check for duplicate files - extract filename from path for comparison
    const isDuplicate = this.uploadedGalleryMedia.some(media => {
      const existingFileName = this.extractFilename(media.fileName);
      return existingFileName === file.name && media.status === 'success';
    });
    if (isDuplicate) {
      this.toastService.error(this.translationService.instant('settings.charity_details_form.gallery.error_file_already_uploaded'));
      return;
    }

    // Upload file via API
    this.isUploadingGalleryMedia = true;

    // Determine fileType based on whether it's an image or video
    const fileType = file.type.startsWith('video/') ? 'ORGANISATION_VIDEO' : 'ORGANISATION_IMAGE';

    const params = {
      entityUuid: this.organisationUuid || '',
      fileType: fileType,
      secondUuid: '',
      file: file
    };

    this.generalService.uploadFile(params).subscribe({
      next: (response: FileUploadResponse) => {
        this.isUploadingGalleryMedia = false;

        // If organisationUuid was null and response has objectUuid, set it
        if (!this.organisationUuid && (response as any).objectUuid) {
          this.organisationUuid = (response as any).objectUuid;
        }

        // Store uploaded media details
        this.uploadedGalleryMedia.push({
          fileName: response.response.fileName || file.name,
          fileUrl: response.response.url,
          fileUuid: response.response.uuid,
          fileSize: response.response.fileSize || file.size,
          fileSizeFormatted: this.formatFileSize(response.response.fileSize || file.size),
          fileType: file.type,
          status: 'success'
        });

        this.toastService.success(this.translationService.instant('settings.charity_details_form.gallery.success_media_uploaded'));
      },
      error: (error) => {
        console.error('Failed to upload gallery media:', error);
        this.isUploadingGalleryMedia = false;
        const errorMessage = this.translationService.instant('error.file_upload_failed');
        this.toastService.error(errorMessage);
        this.uploadedGalleryMedia.push({
          fileName: file.name,
          fileUrl: '',
          fileUuid: '',
          fileSize: file.size,
          fileSizeFormatted: this.formatFileSize(file.size),
          fileType: file.type,
          status: 'error',
          errorMessage
        });
        this.trackFieldError(FormStep.GALLERY, 'organisationImages');
      }
    });
  }

  /**
   * Delete a gallery media file
   * API: DELETE api/v1/general/delete/file?entityUuid={organisationUuid}&fileType=ORGANISATION_IMAGE|ORGANISATION_VIDEO&fileUuid={fileUuid}
   */
  deleteGalleryMedia(index: number): void {
    const media = this.uploadedGalleryMedia[index];

    // If it's a failed upload (no UUID), just remove from the list locally
    if (!media.fileUuid) {
      this.uploadedGalleryMedia.splice(index, 1);
      return;
    }

    // Determine fileType based on whether it's an image or video
    const fileType = this.isVideo(media.fileType) ? 'ORGANISATION_VIDEO' : 'ORGANISATION_IMAGE';

    // Delete file via API
    const params = {
      entityUuid: this.organisationUuid || '',
      fileType: fileType,
      fileUuid: media.fileUuid
    };

    this.generalService.deleteFile(params).subscribe({
      next: () => {
        this.uploadedGalleryMedia.splice(index, 1);
        this.toastService.success(this.translationService.instant('settings.charity_details_form.gallery.success_media_deleted'));
      },
      error: (error) => {
        console.error('Failed to delete gallery media:', error);
        const errorMessage = this.translationService.instant('settings.charity_details_form.gallery.error_media_delete_failed');
        this.toastService.error(errorMessage);
        this.uploadedGalleryMedia[index].status = 'error';
        this.uploadedGalleryMedia[index].errorMessage = errorMessage;
      }
    });
  }

  /**
   * Preview gallery media (open in new tab)
   */
  previewGalleryMedia(media: any): void {
    if (media.status === 'error') {
      return;
    }

    if (media.fileUrl) {
      this.dialog.open(MediaPreviewDialogComponent, {
        data: {
          url: media.fileUrl,
          fileName: media.fileName,
          fileType: media.fileType
        },
        panelClass: 'media-preview-dialog',
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '100vw',
        height: '100vh',
      });
    }
  }

  /**
   * Check if file is an image
   */
  isImage(fileType: string): boolean {
    return fileType?.startsWith('image/') || false;
  }

  /**
   * Check if file is a video
   */
  isVideo(fileType: string): boolean {
    return fileType?.startsWith('video/') || false;
  }

  // ==================== Financial Tab Methods ====================

  /**
   * Add a new budget year
   */
  addNewBudgetYear(): void {
    this.trackTabStart(FormStep.FINANCIALS);
    const newYear: BudgetYear = {
      id: this.generateId(),
      year: '',
      fiscalYearEnd: '',
      currency: '',
      revenueItems: [],
      expenseItems: []
    };

    // Add new year at the top (beginning of array)
    this.budgetYears.unshift(newYear);
    this.currentBudgetYearIndex = 0;

    // Clear any previous errors
    this.clearFinancialsErrors();

    // Trigger change detection
    this.cdr.detectChanges();
  }

  /**
   * Validate current budget year fields
   */
  private validateCurrentBudgetYear(): boolean {
    this.clearFinancialsErrors();
    let isValid = true;

    const currentYear = this.getCurrentBudgetYear();
    if (!currentYear) return true;

    // Store the index of the budget year being validated
    this.currentErrorBudgetYearIndex = this.currentBudgetYearIndex;

    if (this.isFinancialsFieldRequired('budgetYear') && !currentYear.year) {
      this.financialsYearError = this.translationService.instant('settings.charity_details_form.financials.year_required');
      this.trackFieldError(FormStep.FINANCIALS, 'budgetYear');
      isValid = false;
    }

    if (this.isFinancialsFieldRequired('fiscalYearEnd') && !currentYear.fiscalYearEnd) {
      this.financialsFiscalYearError = this.translationService.instant('settings.charity_details_form.financials.fiscal_year_end_required');
      this.trackFieldError(FormStep.FINANCIALS, 'fiscalYearEnd');
      isValid = false;
    }

    if (this.isFinancialsFieldRequired('budgetCurrency') && !currentYear.currency) {
      this.financialsCurrencyError = this.translationService.instant('settings.charity_details_form.financials.currency_required');
      this.trackFieldError(FormStep.FINANCIALS, 'budgetCurrency');
      isValid = false;
    }

    return isValid;
  }

  /**
   * Validate all budget years against the dynamic field configuration from the API.
   * If any year-level field is required and no budget years exist, validation fails.
   */
  private validateAllBudgetYears(): boolean {
    this.clearFinancialsErrors();

    const yearRequired = this.isFinancialsFieldRequired('budgetYear');
    const fiscalYearRequired = this.isFinancialsFieldRequired('fiscalYearEnd');
    const currencyRequired = this.isFinancialsFieldRequired('budgetCurrency');
    const anyPeriodRequired = yearRequired || fiscalYearRequired || currencyRequired;

    const revenueItemNameRequired = this.isFinancialsFieldRequired('revenueItemName');
    const revenueBudgetRequired = this.isFinancialsFieldRequired('revenueBudget');
    const revenueActualsRequired = this.isFinancialsFieldRequired('revenueActuals');
    const anyRevenueRequired = revenueItemNameRequired || revenueBudgetRequired || revenueActualsRequired;

    const expenseItemNameRequired = this.isFinancialsFieldRequired('expenseItemName');
    const expenseBudgetRequired = this.isFinancialsFieldRequired('expenseBudget');
    const expenseActualsRequired = this.isFinancialsFieldRequired('expenseActuals');
    const anyExpenseRequired = expenseItemNameRequired || expenseBudgetRequired || expenseActualsRequired;

    if (this.budgetYears.length === 0) {
      return !anyPeriodRequired && !anyRevenueRequired && !anyExpenseRequired;
    }

    for (let i = 0; i < this.budgetYears.length; i++) {
      const budgetYear = this.budgetYears[i];

      const hasYearError = yearRequired && !budgetYear.year;
      const hasFiscalYearError = fiscalYearRequired && !budgetYear.fiscalYearEnd;
      const hasCurrencyError = currencyRequired && !budgetYear.currency;

      if (hasYearError || hasFiscalYearError || hasCurrencyError) {
        this.financialsYearError = hasYearError ? this.translationService.instant('settings.charity_details_form.financials.year_required') : '';
        this.financialsFiscalYearError = hasFiscalYearError ? this.translationService.instant('settings.charity_details_form.financials.fiscal_year_end_required') : '';
        this.financialsCurrencyError = hasCurrencyError ? this.translationService.instant('settings.charity_details_form.financials.currency_required') : '';
        this.currentErrorBudgetYearIndex = i;
        this.currentBudgetYearIndex = i;
        if (hasYearError) this.trackFieldError(FormStep.FINANCIALS, 'budgetYear');
        if (hasFiscalYearError) this.trackFieldError(FormStep.FINANCIALS, 'fiscalYearEnd');
        if (hasCurrencyError) this.trackFieldError(FormStep.FINANCIALS, 'budgetCurrency');
        return false;
      }

      if (anyRevenueRequired) {
        const hasRevenueItemError = budgetYear.revenueItems.length === 0 ||
          budgetYear.revenueItems.some(item =>
            (revenueItemNameRequired && !item.itemName?.trim()) ||
            (revenueBudgetRequired && item.budget == null) ||
            (revenueActualsRequired && item.actual == null)
          );
        if (hasRevenueItemError) {
          this.currentErrorBudgetYearIndex = i;
          this.currentBudgetYearIndex = i;
          this.financialsItemValidationAttempted = true;
          if (budgetYear.revenueItems.length === 0) {
            this.trackFieldError(FormStep.FINANCIALS, 'revenueItemName');
          } else {
            if (revenueItemNameRequired && budgetYear.revenueItems.some(item => !item.itemName?.trim())) {
              this.trackFieldError(FormStep.FINANCIALS, 'revenueItemName');
            }
            if (revenueBudgetRequired && budgetYear.revenueItems.some(item => item.budget == null)) {
              this.trackFieldError(FormStep.FINANCIALS, 'revenueBudget');
            }
            if (revenueActualsRequired && budgetYear.revenueItems.some(item => item.actual == null)) {
              this.trackFieldError(FormStep.FINANCIALS, 'revenueActuals');
            }
          }
          return false;
        }
      }

      if (anyExpenseRequired) {
        const hasExpenseItemError = budgetYear.expenseItems.length === 0 ||
          budgetYear.expenseItems.some(item =>
            (expenseItemNameRequired && !item.itemName?.trim()) ||
            (expenseBudgetRequired && item.budget == null) ||
            (expenseActualsRequired && item.actual == null)
          );
        if (hasExpenseItemError) {
          this.currentErrorBudgetYearIndex = i;
          this.currentBudgetYearIndex = i;
          this.financialsItemValidationAttempted = true;
          if (budgetYear.expenseItems.length === 0) {
            this.trackFieldError(FormStep.FINANCIALS, 'expenseItemName');
          } else {
            if (expenseItemNameRequired && budgetYear.expenseItems.some(item => !item.itemName?.trim())) {
              this.trackFieldError(FormStep.FINANCIALS, 'expenseItemName');
            }
            if (expenseBudgetRequired && budgetYear.expenseItems.some(item => item.budget == null)) {
              this.trackFieldError(FormStep.FINANCIALS, 'expenseBudget');
            }
            if (expenseActualsRequired && budgetYear.expenseItems.some(item => item.actual == null)) {
              this.trackFieldError(FormStep.FINANCIALS, 'expenseActuals');
            }
          }
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Clear financials validation errors
   */
  private clearFinancialsErrors(): void {
    this.financialsYearError = '';
    this.financialsFiscalYearError = '';
    this.financialsCurrencyError = '';
    this.financialsItemValidationAttempted = false;
    this.currentErrorBudgetYearIndex = -1;
  }

  /**
   * Remove a budget year
   */
  removeBudgetYear(index: number): void {
    this.budgetYears.splice(index, 1);

    // Clear any validation errors
    this.clearFinancialsErrors();

    // Adjust current index if needed
    if (this.currentBudgetYearIndex >= this.budgetYears.length) {
      this.currentBudgetYearIndex = Math.max(0, this.budgetYears.length - 1);
    }

    // Trigger change detection
    this.cdr.detectChanges();
  }

  /**
   * Get available years for dropdown (excluding already used years)
   */
  getAvailableYearItems(index?: number): DropdownItem[] {
    if (!this.yearDropdownItems) return [];

    const currentBudgetYear = index !== undefined ? this.budgetYears[index] : this.getCurrentBudgetYear();
    const usedYears = this.budgetYears
      .filter(by => by.id !== currentBudgetYear?.id) // Exclude current year being edited
      .map(by => by.year)
      .filter(y => y); // Filter out empty strings

    return this.yearDropdownItems.filter(item => !usedYears.includes(item.label));
  }

  /**
   * Handle year selection for specific budget year
   */
  onYearSelectedForBudget(index: number, item: DropdownItem): void {
    const budgetYear = this.budgetYears[index];
    if (budgetYear) {
      this.trackTabStart(FormStep.FINANCIALS);
      budgetYear.year = item.label;
      this.isYearDropdownOpen = false;
      this.financialsYearError = '';

      // Trigger change detection
      this.cdr.detectChanges();
    }
  }

  /**
   * Handle currency selection for specific budget year
   */
  onCurrencySelectedForBudget(index: number, item: DropdownItem): void {
    const budgetYear = this.budgetYears[index];
    if (budgetYear) {
      this.trackTabStart(FormStep.FINANCIALS);
      budgetYear.currency = item.label;
      this.isCurrencyDropdownOpen = false;

      // Clear currency error when a currency is selected
      if (index === this.currentErrorBudgetYearIndex) {
        this.financialsCurrencyError = '';
      }

      // Trigger change detection
      this.cdr.detectChanges();
    }
  }

  /**
   * Get year dropdown item for a budget year
   */
  getYearItemForBudget(budgetYear: BudgetYear): DropdownItem | null {
    if (!budgetYear.year) return null;
    return this.yearDropdownItems.find(item => item.label === budgetYear.year) || null;
  }

  /**
   * Get currency dropdown item for a budget year
   */
  getCurrencyItemForBudget(budgetYear: BudgetYear): DropdownItem | null {
    if (!budgetYear.currency) return null;
    return this.currencyDropdownItems.find(item => item.label === budgetYear.currency) || null;
  }

  /**
   * Get fiscal year end date for datepicker
   */
  getFiscalYearEndDate(budgetYear: BudgetYear): Date | null {
    if (!budgetYear.fiscalYearEnd) return null;
    return new Date(budgetYear.fiscalYearEnd);
  }

  /**
   * Handle fiscal year end change
   */
  onFiscalYearEndChange(index: number, date: Date | null): void {
    const budgetYear = this.budgetYears[index];
    if (budgetYear && date) {
      this.trackTabStart(FormStep.FINANCIALS);
      const yearValue = budgetYear.year || new Date().getFullYear().toString();
      const combinedDate = new Date(
        parseInt(yearValue),
        date.getMonth(),
        date.getDate()
      );
      budgetYear.fiscalYearEnd = combinedDate.toISOString();

      // Clear fiscal year error when a date is selected
      if (index === this.currentErrorBudgetYearIndex) {
        this.financialsFiscalYearError = '';
      }

      // Trigger change detection
      this.cdr.detectChanges();
    }
  }

  /**
   * Get fiscal year picker start date based on budget year
   */
  getFiscalYearPickerStartDate(budgetYear: BudgetYear): Date {
    if (budgetYear.year) {
      const year = parseInt(budgetYear.year);
      const month = budgetYear.fiscalYearEnd ? new Date(budgetYear.fiscalYearEnd).getMonth() : 0;
      return new Date(year, month, 1);
    }
    return new Date();
  }

  /**
   * Switch to a different budget year
   */
  switchBudgetYear(index: number): void {
    if (index === this.currentBudgetYearIndex) return;

    // Save current form data before switching
    this.saveBudgetYearFromForm();

    // Switch to the selected year
    this.currentBudgetYearIndex = index;
    this.loadBudgetYearToForm();

    // Clear any errors
    this.clearFinancialsErrors();
  }

  /**
   * Format fiscal year end for display
   */
  formatFiscalYearEnd(fiscalYearEnd: string): string {
    if (!fiscalYearEnd) return '';

    try {
      const date = new Date(fiscalYearEnd);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${month}/${day}`;
    } catch (e) {
      return '';
    }
  }

  /**
   * Load current budget year data to form
   */
  private loadBudgetYearToForm(): void {
    const currentYear = this.getCurrentBudgetYear();
    if (currentYear) {
      // Convert fiscalYearEnd string to Date object for the datepicker
      const fiscalYearDate = currentYear.fiscalYearEnd ? new Date(currentYear.fiscalYearEnd) : null;

      this.financialsForm.patchValue({
        year: currentYear.year || '',
        fiscalYearEnd: fiscalYearDate,
        currency: currentYear.currency || ''
      });

      // Update selected dropdown items
      if (currentYear.year) {
        this.selectedYearItem = this.yearDropdownItems.find(item => item.label === currentYear.year) || null;

        // Update datepicker start date to use the selected year (for leap year handling)
        const selectedYear = parseInt(currentYear.year);
        if (!isNaN(selectedYear)) {
          this.fiscalYearPickerStartDate = new Date(
            selectedYear,
            fiscalYearDate ? fiscalYearDate.getMonth() : 0,
            1
          );
        }
      } else {
        // Clear selected year item if no year
        this.selectedYearItem = null;
        // Reset to current year for datepicker
        this.fiscalYearPickerStartDate = new Date();
      }

      if (currentYear.currency) {
        this.selectedCurrencyItem = this.currencyDropdownItems.find(item => item.label === currentYear.currency) || null;
      } else {
        // Clear selected currency item if no currency
        this.selectedCurrencyItem = null;
      }
    }
  }

  /**
   * Get current budget year
   */
  getCurrentBudgetYear(): BudgetYear | null {
    return this.budgetYears[this.currentBudgetYearIndex] || null;
  }

  /**
   * Save current form values to budget year
   */
  saveBudgetYearFromForm(): void {
    const currentYear = this.getCurrentBudgetYear();
    if (currentYear) {
      const formValue = this.financialsForm.value;
      currentYear.year = formValue.year;

      // Convert Date object to ISO string, combining with the selected year from dropdown
      if (formValue.fiscalYearEnd instanceof Date) {
        const pickedDate = formValue.fiscalYearEnd;
        const yearValue = formValue.year || new Date().getFullYear().toString();

        // Create a date with the selected year from dropdown and the month/day from the datepicker
        const combinedDate = new Date(
          parseInt(yearValue),
          pickedDate.getMonth(),
          pickedDate.getDate()
        );
        currentYear.fiscalYearEnd = combinedDate.toISOString();
      } else if (formValue.fiscalYearEnd) {
        currentYear.fiscalYearEnd = formValue.fiscalYearEnd;
      }

      currentYear.currency = formValue.currency;
    }
  }

  /**
   * Populate dropdown items from API response
   */
  private populateDropdownItems(): void {
    if (this.dropdowns) {
      // Populate year dropdown items
      this.yearDropdownItems = this.dropdowns.budgetYears.map(year => ({
        id: year.uuid,
        label: year.name
      }));

      // Populate currency dropdown items
      this.currencyDropdownItems = this.dropdowns.currencies.map(currency => ({
        id: currency.uuid,
        label: currency.name
      }));
    }
  }

  /**
   * Populate all forms with loaded organisation data
   */
  private populateFormsWithOrganisationData(): void {
    if (!this.organisationData) return;

    // Loading saved data must never register as the user "starting" a tab —
    // see the isPopulatingForm guard on the detailsForm/teamForm valueChanges
    // subscriptions in ngOnInit.
    this.isPopulatingForm = true;
    this.populateDetailsForm();
    this.populateTeamForm();
    this.populateFinancialsForm();
    this.populateDocuments();
    this.populateGallery();
    this.isPopulatingForm = false;
  }

  /**
   * Populate details form with organisation data
   * Handles nested FormGroups: basicInformation, address, socialMediaLinks, charityDetails
   */
  private populateDetailsForm(): void {
    const data = this.organisationData;

    // Extract multi-select values
    this.selectedCategories = data.categories?.map((c: any) => c.uuid) || [];
    this.selectedCountries = data.organisationInfluencedCountries?.map((c: any) => c.uuid) || [];
    // Seed the derived region order from the saved regionOrder (sorted by position)
    // BEFORE reconciliation, so a user's drag order survives an edit reload.
    this.selectedRegions = (data.regionOrder || [])
      .slice()
      .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
      .map((r: any) => r.uuid);
    this.selectedCause = data.cause?.uuid || null;
    this.scheduleCategoryChipsUpdate();
    this.scheduleCountryChipsUpdate();
    this.updateCauseDisplay();
    this.updateCategoryDisplay();
    this.updateInfluencedCountriesDisplay();

    // Populate basicInformation
    this.detailsForm.get('basicInformation')?.patchValue({
      charityProfile: data.mandatoryProfile?.uuid || '',
      organizationName: data.organisationName || '',
      private: data.isPrivate || false,
    });

    // Populate address — country is stored as a name in the API but the select binds to UUIDs
    const countryUuid = this.dropdowns?.countries.find(
      c => c.name === data.address?.country
    )?.uuid || null;
    this.detailsForm.get('address')?.patchValue({
      streetName: data.address?.streetName || '',
      city: data.address?.city || '',
      stateProvince: data.address?.stateProvince || '',
      country: countryUuid,
      landmark: data.address?.landmarkAddress || ''
    });

    // Populate socialMediaLinks
    this.detailsForm.get('socialMediaLinks')?.patchValue({
      facebook: data.socialMediaFacebook || '',
      twitter: data.socialMediaTwitter || '',
      instagram: data.socialMediaInstagram || '',
      linkedin: data.socialMediaLinkedin || ''
    });

    // Snapshot the server-side Giving Link — buildGivingLinkPayloadValue() sends this
    // back unchanged for users who aren't allowed to edit it.
    this.serverGivingLink = data.givingLink || '';

    // Populate charityDetails
    this.detailsForm.get('charityDetails')?.patchValue({
      website: data.organisationWebsite || '',
      targetPopulation: data.targetPopulation || '',
      yearFounded: data.yearFounded || null,
      givingLink: data.givingLink || '',
      executiveSummary: data.executiveSummary || '',
      visionStatement: data.visionStatement || '',
      missionStatement: data.missionStatement || '',
      history: data.history || ''
    });

    // Load uploaded media files
    if (data.organisationLogo) {
      this.uploadedLogo = {
        fileName: this.extractFileName(data.organisationLogo.filename || 'logo'),
        fileUrl: data.organisationLogo.url || '',
        fileUuid: data.organisationLogo.uuid || ''
      };
    }

    if (data.organisationCoverImage) {
      this.uploadedCoverImage = {
        fileName: this.extractFileName(data.organisationCoverImage.filename || 'cover'),
        fileUrl: data.organisationCoverImage.url || '',
        fileUuid: data.organisationCoverImage.uuid || ''
      };
    }
  }

  /**
   * Populate team form with organisation team data
   */
  private populateTeamForm(): void {
    const data = this.organisationData;

    // Populate charity leader
    if (data.organisationLeader) {
      this.charityLeader = { ...data.organisationLeader };

      // Load leader picture
      if (data.organisationLeader.profilePicture) {
        this.uploadedLeaderPicture = {
          fileName: data.organisationLeader.profilePicture.filename || 'leader',
          fileUrl: data.organisationLeader.profilePicture.url || '',
          fileUuid: data.organisationLeader.profilePicture.uuid || ''
        };
      }

      // Populate leader form fields
      this.teamForm.patchValue({
        leaderName: data.organisationLeader.fullName || '',
        leaderEmail: data.organisationLeader.email || '',
        leaderLinkedin: data.organisationLeader.linkedinProfile || '',
        leaderBio: data.organisationLeader.bio || ''
      });
    }

    // Populate number of employees
    this.teamForm.patchValue({
      numberOfTeamMembers: data.numberOfEmployees?.toString() || '0'
    });

    // Load executive team members
    this.executiveTeamMembers = data.executiveTeams?.map((member: any) => ({
      ...member,
      profilePicture: member.profilePicture ? {
        ...member.profilePicture,
        fileName: member.profilePicture.fileName || (member.profilePicture.url ? member.profilePicture.url.split('/').pop() : '')
      } : member.profilePicture
    })) || [];
    this.existingExecutiveFormGroups = this.executiveTeamMembers.map(m => this.buildExecutiveMemberFormGroup(m));

    // Load board members
    this.boardMembers = data.boardMembers?.map((member: any) => ({
      ...member,
      profilePicture: member.profilePicture ? {
        ...member.profilePicture,
        fileName: member.profilePicture.fileName || (member.profilePicture.url ? member.profilePicture.url.split('/').pop() : '')
      } : member.profilePicture
    })) || [];
    this.existingBoardMemberFormGroups = this.boardMembers.map(m => this.buildBoardMemberFormGroup(m));

    // Load partner organisations
    this.partnerOrganizations = data.partnerOrganisations?.map((partner: any) => ({ ...partner })) || [];
    this.existingPartnerFormGroups = this.partnerOrganizations.map(p => this.buildPartnerFormGroup(p));
  }

  /**
   * Populate financials form with budget data
   * Maps budgetOrganisations to BudgetYear structure
   */
  private populateFinancialsForm(): void {
    const data = this.organisationData;

    if (!data.budgetOrganisations || data.budgetOrganisations.length === 0) {
      return;
    }

    this.budgetYears = data.budgetOrganisations.map((budgetOrg: any) => {
      // Parse fiscal year end from "DD-MMM" format to ISO date string
      let fiscalYearEndDate = '';
      if (budgetOrg.fiscalYearEnd) {
        try {
          // API returns format: "DD-MMM" (e.g., "12-Feb")
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const [dayStr, monthStr] = budgetOrg.fiscalYearEnd.split('-');
          const day = parseInt(dayStr);
          const month = monthNames.indexOf(monthStr);

          if (month === -1) {
            throw new Error(`Invalid month name: ${monthStr}`);
          }

          // Use the budget year if available, otherwise current year
          const year = budgetOrg.budgetYear?.name ? parseInt(budgetOrg.budgetYear.name) : new Date().getFullYear();

          const date = new Date(year, month, day);
          fiscalYearEndDate = date.toISOString();
        } catch (e) {
          console.error('Error parsing fiscal year end:', e, 'Value:', budgetOrg.fiscalYearEnd);
        }
      }

      // Map revenue and expense items
      const revenueItems = (budgetOrg.budgetRevenues || []).map((item: any) => ({
        id: this.generateId(),
        itemName: item.itemName || '',
        budget: item.budget || null,
        actual: item.actuals || null
      }));

      const expenseItems = (budgetOrg.budgetExpenses || []).map((item: any) => ({
        id: this.generateId(),
        itemName: item.itemName || '',
        budget: item.budget || null,
        actual: item.actuals || null
      }));

      return {
        id: this.generateId(),
        year: budgetOrg.budgetYear?.name || '',
        fiscalYearEnd: fiscalYearEndDate,
        currency: budgetOrg.budgetCurrency?.name || '',
        revenueItems: revenueItems,
        expenseItems: expenseItems
      };
    });

    if (this.budgetYears.length > 0) {
      this.currentBudgetYearIndex = 0;
    }
  }

  /**
   * Populate documents array with existing organisation documents
   */
  private populateDocuments(): void {
    const data = this.organisationData;

    if (!data.organisationDocuments || data.organisationDocuments.length === 0) {
      return;
    }

    this.uploadedDocuments = data.organisationDocuments.map((docItem: any) => {
      const doc = docItem.documentDto || docItem;

      // Use fileSize from API response if available, otherwise default to 0
      const fileSize = doc.fileSize || doc.size || 0;

      return {
        fileName: doc.filename || 'document',
        fileUrl: doc.url || '',
        fileUuid: doc.uuid || '',
        fileSize: fileSize,
        fileSizeFormatted: fileSize > 0 ? this.formatFileSize(fileSize) : '',
        status: 'success' as const
      };
    });
  }

  /**
   * Populate gallery media array with existing organisation images and videos
   * Excludes cover images (cover: true) as they are handled in the Details tab
   */
  private populateGallery(): void {
    const data = this.organisationData;
    this.uploadedGalleryMedia = [];

    // Load video first (singular - organisationVideo, not organisationVideos)
    // Always put video at the top of the list
    if (data.organisationVideo) {
      const media = data.organisationVideo.mediaDto || data.organisationVideo;
      const fileSize = media.fileSize || media.size || 0;

      this.uploadedGalleryMedia.push({
        fileName: media.filename || 'video',
        fileUrl: media.url || '',
        fileUuid: media.uuid || '',
        fileSize: fileSize,
        fileSizeFormatted: fileSize > 0 ? this.formatFileSize(fileSize) : '',
        fileType: media.type || 'video/mp4',
        status: 'success' as const
      });
    }

    // Load images (excluding cover images), sorted by position
    if (data.organisationImages && data.organisationImages.length > 0) {
      const images = data.organisationImages
        .filter((imageItem: any) => {
          const media = imageItem.mediaDto || imageItem;
          // Exclude cover images (they're handled in Details tab)
          return !media.cover;
        })
        .sort((a: any, b: any) => {
          // Sort by position (lowest first)
          const positionA = (a.mediaDto?.position ?? a.position) ?? 999;
          const positionB = (b.mediaDto?.position ?? b.position) ?? 999;
          return positionA - positionB;
        })
        .map((imageItem: any) => {
          const media = imageItem.mediaDto || imageItem;
          const fileSize = media.fileSize || media.size || 0;

          return {
            fileName: media.filename || 'image',
            fileUrl: media.url || '',
            fileUuid: media.uuid || '',
            fileSize: fileSize,
            fileSizeFormatted: fileSize > 0 ? this.formatFileSize(fileSize) : '',
            fileType: media.type || 'image/jpeg',
            status: 'success' as const
          };
        });
      this.uploadedGalleryMedia.push(...images);
    }
  }

  /**
   * Extract filename from a full path
   * Example: "716ebe93-f8a5-47de-b29e-ce4094ea7bf0/organisationDocument/file.pdf" -> "file.pdf"
   */
  private extractFilename(path: string): string {
    if (!path) {
      return '';
    }
    const parts = path.replace(/\\/g, '/').split('/');
    return parts[parts.length - 1] || path;
  }


  /**
   * Toggle year dropdown
   */
  toggleYearDropdown(index?: number): void {
    if (index !== undefined) {
      this.currentBudgetYearIndex = index;
    }
    this.isYearDropdownOpen = !this.isYearDropdownOpen;
    if (this.isYearDropdownOpen) {
      this.isCurrencyDropdownOpen = false;
    }
  }

  /**
   * Toggle currency dropdown
   */
  toggleCurrencyDropdown(index?: number): void {
    if (index !== undefined) {
      this.currentBudgetYearIndex = index;
    }
    this.isCurrencyDropdownOpen = !this.isCurrencyDropdownOpen;
    if (this.isCurrencyDropdownOpen) {
      this.isYearDropdownOpen = false;
    }
  }

  /**
   * Handle year selection
   */
  onYearSelected(item: DropdownItem): void {
    this.selectedYearItem = item;
    this.financialsForm.patchValue({ year: item.label });
    this.isYearDropdownOpen = false;

    // Clear year error when a year is selected
    this.financialsYearError = '';

    // Update the datepicker to use the selected year (for leap year handling)
    const selectedYear = parseInt(item.label);
    if (!isNaN(selectedYear)) {
      const currentDate = this.fiscalYearPickerStartDate;
      this.fiscalYearPickerStartDate = new Date(
        selectedYear,
        currentDate.getMonth(),
        1 // Start at first day of month
      );
    }

    this.saveBudgetYearFromForm();
  }

  /**
   * Handle currency selection
   */
  onCurrencySelected(item: DropdownItem): void {
    this.selectedCurrencyItem = item;
    this.financialsForm.patchValue({ currency: item.label });
    this.isCurrencyDropdownOpen = false;
    this.saveBudgetYearFromForm();
  }

  /**
   * Get selected year label
   */
  getSelectedYearLabel(): string {
    return this.selectedYearItem?.label || this.financialsForm.get('year')?.value || '';
  }

  /**
   * Get selected currency label
   */
  getSelectedCurrencyLabel(): string {
    return this.selectedCurrencyItem?.label || this.financialsForm.get('currency')?.value || '';
  }

  /**
   * Add revenue item to specific budget year
   */
  addRevenueItem(budgetYearIndex: number): void {
    const budgetYear = this.budgetYears[budgetYearIndex];

    if (!budgetYear) {
      return;
    }

    this.trackTabStart(FormStep.FINANCIALS);

    // Add empty item that user can fill in
    const newItem: BudgetItem = {
      id: this.generateId(),
      itemName: '',
      budget: null,
      actual: null
    };

    budgetYear.revenueItems.push(newItem);
    this.cdr.detectChanges();
  }

  /**
   * Delete revenue item from specific budget year
   */
  deleteRevenueItem(budgetYearIndex: number, itemIndex: number): void {
    const budgetYear = this.budgetYears[budgetYearIndex];
    if (budgetYear) {
      budgetYear.revenueItems.splice(itemIndex, 1);
      this.cdr.detectChanges();
    }
  }

  /**
   * Add expense item to specific budget year
   */
  addExpenseItem(budgetYearIndex: number): void {
    const budgetYear = this.budgetYears[budgetYearIndex];

    if (!budgetYear) {
      return;
    }

    this.trackTabStart(FormStep.FINANCIALS);

    // Add empty item that user can fill in
    const newItem: BudgetItem = {
      id: this.generateId(),
      itemName: '',
      budget: null,
      actual: null
    };

    budgetYear.expenseItems.push(newItem);
    this.cdr.detectChanges();
  }

  /**
   * Delete expense item from specific budget year
   */
  deleteExpenseItem(budgetYearIndex: number, itemIndex: number): void {
    const budgetYear = this.budgetYears[budgetYearIndex];
    if (budgetYear) {
      budgetYear.expenseItems.splice(itemIndex, 1);
      this.cdr.detectChanges();
    }
  }

  /**
   * Calculate total revenue budget for specific year
   */
  getTotalRevenueBudget(budgetYearIndex: number): number {
    const budgetYear = this.budgetYears[budgetYearIndex];
    if (!budgetYear) return 0;

    return budgetYear.revenueItems.reduce((sum, item) => sum + (item.budget || 0), 0);
  }

  /**
   * Calculate total revenue actual for specific year
   */
  getTotalRevenueActual(budgetYearIndex: number): number {
    const budgetYear = this.budgetYears[budgetYearIndex];
    if (!budgetYear) return 0;

    return budgetYear.revenueItems.reduce((sum, item) => sum + (item.actual || 0), 0);
  }

  /**
   * Calculate total expense budget for specific year
   */
  getTotalExpenseBudget(budgetYearIndex: number): number {
    const budgetYear = this.budgetYears[budgetYearIndex];
    if (!budgetYear) return 0;

    return budgetYear.expenseItems.reduce((sum, item) => sum + (item.budget || 0), 0);
  }

  /**
   * Calculate total expense actual for specific year
   */
  getTotalExpenseActual(budgetYearIndex: number): number {
    const budgetYear = this.budgetYears[budgetYearIndex];
    if (!budgetYear) return 0;

    return budgetYear.expenseItems.reduce((sum, item) => sum + (item.actual || 0), 0);
  }

  /**
   * Calculate net gain/loss budget for specific year
   */
  getNetGainLossBudget(budgetYearIndex: number): number {
    return this.getTotalRevenueBudget(budgetYearIndex) - this.getTotalExpenseBudget(budgetYearIndex);
  }

  /**
   * Calculate net gain/loss actual for specific year
   */
  getNetGainLossActual(budgetYearIndex: number): number {
    return this.getTotalRevenueActual(budgetYearIndex) - this.getTotalExpenseActual(budgetYearIndex);
  }

  /**
   * Format currency value
   */
  formatCurrency(value: number | null): string {
    if (value === null || value === undefined) return '0.00';
    return value.toFixed(2);
  }

  onNumberOnlyKeydown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];
    if (allowedKeys.includes(event.key)) return;
    if (event.ctrlKey || event.metaKey) return;
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onNumberOnlyPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    if (!/^\d+$/.test(pasted)) {
      event.preventDefault();
    }
  }

  onYearInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value.length > 4) {
      const truncated = input.value.slice(0, 4);
      input.value = truncated;
      this.detailsForm.get('charityDetails.yearFounded')?.setValue(parseInt(truncated, 10), { emitEvent: false });
    }
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Update revenue item for specific budget year
   */
  updateRevenueItem(budgetYearIndex: number, itemIndex: number, field: keyof BudgetItem, value: any): void {
    const budgetYear = this.budgetYears[budgetYearIndex];
    if (budgetYear && budgetYear.revenueItems[itemIndex]) {
      (budgetYear.revenueItems[itemIndex] as any)[field] = value;
      this.cdr.detectChanges();
    }
  }

  /**
   * Update expense item for specific budget year
   */
  updateExpenseItem(budgetYearIndex: number, itemIndex: number, field: keyof BudgetItem, value: any): void {
    const budgetYear = this.budgetYears[budgetYearIndex];
    if (budgetYear && budgetYear.expenseItems[itemIndex]) {
      (budgetYear.expenseItems[itemIndex] as any)[field] = value;
      this.cdr.detectChanges();
    }
  }

  /**
   * Build organisationImages payload for create/update API.
   * Only gallery images are included here (cover: false). The cover image is
   * handled separately (like the logo) via its own upload (ORGANISATION_COVER_IMAGE)
   * and the organisationCoverImage object, so it is no longer added to this list.
   */
  private buildOrganisationImagesPayload(): OrganisationImageItem[] {
    const images: OrganisationImageItem[] = [];
    let position = 0;

    this.uploadedGalleryMedia
      .filter(media => media.status === 'success' && !this.isVideo(media.fileType))
      .forEach(media => {
        images.push({
          mediaDto: {
            fileType: 'ORGANISATION_IMAGE',
            allowedFileType: 'IMG',
            uuid: media.fileUuid,
            fileName: media.fileName,
            url: media.fileUrl,
            cover: false,
            pendingUpload: false,
            isFormatValid: true,
            progress: 0,
            position
          },
          position
        });
        position++;
      });

    return images;
  }

  /**
   * Prepare budget payload for API submission
   * Transforms budgetYears array to API format
   * Filters out completely blank budget years (no data in any field)
   */
  prepareBudgetPayload(): any[] {
    // Filter out completely blank budget years
    const validBudgetYears = this.budgetYears.filter(budgetYear => {
      // Check if main fields have values
      const hasMainFields = budgetYear.year || budgetYear.fiscalYearEnd || budgetYear.currency;

      // Check if any revenue item has data
      const hasRevenueData = budgetYear.revenueItems.some(item =>
        item.itemName || item.budget !== null || item.actual !== null
      );

      // Check if any expense item has data
      const hasExpenseData = budgetYear.expenseItems.some(item =>
        item.itemName || item.budget !== null || item.actual !== null
      );

      // Keep budget year if it has any data in any field
      return hasMainFields || hasRevenueData || hasExpenseData;
    });

    return validBudgetYears.map((budgetYear, index) => {
      // Get UUID from year dropdown items
      const yearItem = this.yearDropdownItems.find(item => item.label === budgetYear.year);
      const budgetYearUUID = yearItem?.id || '';

      // Get UUID from currency dropdown items
      const currencyItem = this.currencyDropdownItems.find(item => item.label === budgetYear.currency);
      const currencyUUID = currencyItem?.id || '';

      // Format fiscal year end from ISO string to "DD-MMM" format
      let fiscalYearEnd = '';
      if (budgetYear.fiscalYearEnd) {
        try {
          const date = new Date(budgetYear.fiscalYearEnd);
          const day = date.getDate().toString().padStart(2, '0');
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const month = monthNames[date.getMonth()];
          fiscalYearEnd = `${day}-${month}`;
        } catch (e) {
          console.error('Error formatting fiscal year end:', e);
        }
      }

      // Calculate totals for this budget year (directly from budgetYear object)
      const totalBudgetRevenues = budgetYear.revenueItems.reduce((sum, item) => sum + (item.budget || 0), 0);
      const totalActualsRevenues = budgetYear.revenueItems.reduce((sum, item) => sum + (item.actual || 0), 0);
      const totalBudgetExpenses = budgetYear.expenseItems.reduce((sum, item) => sum + (item.budget || 0), 0);
      const totalActualsExpenses = budgetYear.expenseItems.reduce((sum, item) => sum + (item.actual || 0), 0);
      const netGainLossBudget = totalBudgetRevenues - totalBudgetExpenses;
      const netGainLossActuals = totalActualsRevenues - totalActualsExpenses;

      // Map revenue items to budgetRevenues format with positions
      const budgetRevenues = budgetYear.revenueItems.map((item, itemIndex) => ({
        uuid: null,
        budget: item.budget || 0,
        actuals: item.actual || 0,
        itemName: item.itemName || '',
        position: itemIndex + 1 // 1-indexed position
      }));

      // Map expense items to budgetExpenses format with positions
      const budgetExpenses = budgetYear.expenseItems.map((item, itemIndex) => ({
        uuid: null,
        budget: item.budget || 0,
        actuals: item.actual || 0,
        itemName: item.itemName || '',
        position: itemIndex + 1 // 1-indexed position
      }));

      // Build the budget organisation payload
      return {
        budgetRevenues: budgetRevenues,
        budgetExpenses: budgetExpenses,
        budgetYearUUID: budgetYearUUID,
        fiscalYearEnd: fiscalYearEnd,
        totalActualsExpenses: totalActualsExpenses,
        totalActualsRevenues: totalActualsRevenues,
        totalBudgetExpenses: totalBudgetExpenses,
        totalBudgetRevenues: totalBudgetRevenues,
        netGainLossBudget: netGainLossBudget,
        netGainLossActuals: netGainLossActuals,
        uuid: null,
        budgetCurrency: {
          uuid: currencyUUID,
          name: budgetYear.currency || '',
          disabled: false
        },
        budgetYear: {
          uuid: budgetYearUUID,
          name: budgetYear.year || '',
          disabled: false
        },
        position: index + 1 // 1-indexed position for budget organisations
      };
    });
  }
}
